/*
  Cart redesign behaviours on the Rebuy Smart Cart V2.
  Rebuy re-renders its markup on every cart change, so everything here is idempotent and re-applied
  whenever the cart DOM changes. Wording comes from window.__cartRedesign (snippets/cart-redesign.liquid).

  Phase 1
  - Title: item count after the title ("My Cart (2)").
  - Progress bar: "Spend $X" under each milestone, "Unlocked" once reached; "Spend more, unlock more" when empty.
  - Empty cart: icon, heading, "Popular right now" and Continue Shopping in place of Rebuy's empty-cart text.
  - Footer: Afterpay line reworded with the Afterpay badge; shipping note under it.
  Kurt's pass (22 Oct, every width): the progress band folds with the scroll (applyBand), "Free shipping included"
  once reached, second picture on hover for recommendations, a gift box stays open while USO answers.
  Round 2 (phones only, under 768px)
  - "Add a little more": best sellers that close the gap to the next free gift or free shipping (applyFiller).
  - Shorter lines and footer, 44px tap targets: CSS only (end of cart-redesign.css). Gift tiles stay 2.5 wide.
  Phase 2
  - Cart lines: "Size" before the size, line total beside the stepper, bin icon at quantity 1 (data attributes only).
  - Gift lines: "Free gift" pill, quantity, FREE and a Change link that takes the gift out and reopens its box.
  - Gift boxes: Daniel's headings on Rebuy's own boxes; a locked box (greyed gifts, padlock, amount to go) while a
    tier is not reached; "come out" wording when a claimed gift drops out. Rebuy's widget scripts still hide a box
    once its gift is in the cart and remove the gift when the cart drops under the tier.
  - Recommendations: Sale badge. The + is Rebuy's own button restyled (products with options open Rebuy's picker).
  - Footer: "You saved" under the total. Login and Yarn Rewards points line in the empty cart and under the items.
*/
(function () {
  'use strict';

  var cfg = window.__cartRedesign || {};
  var copy = cfg.copy || {};
  var assets = cfg.assets || {};
  var root = null;
  var queued = false;

  function smartCart() {
    return window.Rebuy && window.Rebuy.SmartCart;
  }

  // Round 2 changes are for phones only; the same width as the CSS (max-width: 767px).
  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  function isPhone() {
    return !!(PHONE && PHONE.matches);
  }

  // The bar Rebuy is actually drawing (it picks one per location). Amounts are only shown for AUD price tiers,
  // since Rebuy converts thresholds for other currencies.
  function tiers() {
    try {
      var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (currency && currency !== 'AUD') return [];
      return smartCart().progressBar.tiers || [];
    } catch (e) {
      return [];
    }
  }

  // Whole dollars without cents ("$100"), otherwise two decimals.
  function money(dollars) {
    var n = Number(dollars);
    if (!isFinite(n)) return '';
    return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
  }

  // Cents in the shop's money format ("$8.88").
  function cents(value) {
    try { return smartCart().formatMoney(value); } catch (e) { return '$' + (value / 100).toFixed(2); }
  }

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? vars[k] : m; });
  }

  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  function dropAttr(el, name) {
    if (el && el.hasAttribute(name)) el.removeAttribute(name);
  }

  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function itemCount() {
    try {
      var n = window.Rebuy.Cart.itemCount();
      return typeof n === 'number' ? n : null;
    } catch (e) {
      return null;
    }
  }

  function shopifyCart() {
    try { return window.Rebuy.Cart.getCart() || null; } catch (e) { return null; }
  }

  // Lines in the order Rebuy draws them.
  function lines() {
    try {
      var sc = smartCart();
      return (sc.items() || []).filter(function (item) { return !(sc.isHiddenItem && sc.isHiddenItem(item)); });
    } catch (e) {
      return [];
    }
  }

  function isAud() {
    var currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    return !currency || currency === 'AUD';
  }

  // A free-gift copy (gift handles end in -free-gift), whatever its price.
  function isGiftCopy(item) {
    if (!item) return false;
    if (/-free-gift$/.test(item.handle || '')) return true;
    var tags = item.product && item.product.tags;
    if (typeof tags === 'string') tags = tags.split(/,\s*/);
    return Array.isArray(tags) && tags.indexOf('freegift') > -1;
  }

  // A free-gift copy at $0: priced $0 (the gift guard's test), or priced and brought to $0 by Rebuy's "Save 100%"
  // discount (a copy someone gave a price, e.g. the Pride lanyard copy at $10, 21 Oct).
  function isGift(item) {
    return !!item && (item.original_price === 0 || item.final_price === 0) && isGiftCopy(item);
  }

  // The cart total Rebuy judges its gift boxes and progress bar on (after order discounts), in cents.
  function rebuySubtotal() {
    try { var n = window.Rebuy.Cart.subtotal(); return typeof n === 'number' ? n : null; } catch (e) { return null; }
  }

  // Gift copies are named "… - FREE GIFT" in the shop; in the cart the pill, FREE and the box say so already.
  var GIFT_SUFFIX = /\s*(?:[-–—]\s*free\s+gift|\(\s*free\s+gift\s*\))\s*$/i;

  function giftName(name) {
    return String(name || '').replace(GIFT_SUFFIX, '');
  }

  // Trims the ending from the text Rebuy drew, editing its last text node in place (so Rebuy's own updates still
  // land on the same node, and this simply runs again).
  function trimGiftName(node) {
    if (!node) return;
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    var text, last = null;
    while ((text = walker.nextNode())) { if (text.nodeValue.trim()) last = text; }
    if (!last) return;
    var trimmed = last.nodeValue.replace(GIFT_SUFFIX, '');
    if (trimmed !== last.nodeValue) last.nodeValue = trimmed;
  }

  function giftWidgetOf(item) {
    return (item && item.properties && item.properties._widget_id) ? String(item.properties._widget_id) : '';
  }

  function applyTitle() {
    var title = root.querySelector('.rebuy-cart__title');
    var n = itemCount();
    if (title && n !== null) setAttr(title, 'data-yc-count', String(n));
  }

  function applyProgress() {
    var list = tiers();
    var steps = root.querySelectorAll('.rebuy-cart__progress-step');
    for (var i = 0; i < steps.length; i++) {
      var label = steps[i].querySelector('.rebuy-cart__progress-step-label');
      var tier = list[i];
      if (!label || !tier || tier.minimum == null || tier.unlock_condition_type !== 'price') continue;
      setAttr(label, 'data-yc-spend', (copy.spendPrefix || 'Spend ') + money(tier.minimum));
      setAttr(label, 'data-yc-unlocked', copy.unlocked || 'Unlocked');
    }
    var prompt = root.querySelector('.rebuy-cart__progress-bar-prompt');
    if (prompt && copy.emptyPrompt) setAttr(prompt, 'data-yc-empty', copy.emptyPrompt);
    // Every milestone reached: one short line in place of Rebuy's last-tier wording (applyDealBar does the same with a deal).
    var all = steps.length > 0 && [].every.call(steps, function (s) { return s.classList.contains('complete'); });
    if (prompt && all && copy.allUnlocked) setAttr(prompt, 'data-yc-all', copy.allUnlocked);
    else dropAttr(prompt, 'data-yc-all');
  }

  // The free-shipping tier is reached: Rebuy's shipping tier (by type, not by its label) against the bar's total
  // (before any deal, as the shipping app charges). AUD only: tiers() is empty for other currencies.
  function freeShipping() {
    var sc = smartCart();
    var base = null;
    try { base = sc.progressBarSubtotal(); } catch (e) {}
    if (typeof base !== 'number') return false;
    return tiers().some(function (t) {
      if (t.type !== 'shipping' || t.unlock_condition_type !== 'price' || t.minimum == null) return false;
      var min = null;
      try { min = sc.getTierMinimum(t); } catch (e) {}
      if (typeof min !== 'number') min = Math.round(t.minimum * 100);
      return base >= min;
    });
  }

  // ---------- Progress band: folds with the scroll ----------
  // The milestone names under the track overlap the top of the list (the band's negative bottom margin and the
  // list's top padding, both --yc-fold), so nothing in the layout moves while scrolling. Over the first --yc-fold of
  // scroll the band's bottom edge is clipped up by exactly the distance scrolled and the names fade (--yc-p, 0 to 1),
  // so the band's lower part rolls up with the list, and unrolls the same way back at the top (Kurt, 22 Oct: a timed
  // fold past a threshold felt clunky). Only clip-path and opacity change while scrolling, which phones redraw
  // without working the layout out again. Kurt's folded frame, 21 Oct: message, track and dots stay.

  var bandFold = 0;

  // The fold: from the band's bottom up to 6px under its dots (the folded band's bottom padding). The figure only
  // changes if the milestone names wrap differently; a closed cart measures nothing, so the last figure stays. An open
  // cart with no band at all drops it, so the list doesn't keep an empty strip at its top. Runs last in apply(), so
  // its two measurements come after every other step's changes.
  function applyBand() {
    var bar = root.querySelector('[data-rebuy-component="progress-bar"]');
    var dot = bar && bar.querySelector('.rebuy-cart__progress-step-icon');
    if (!bar && root.classList.contains('is-visible') && root.querySelector('[data-rebuy-cart-scroll-area]')) {
      bandFold = 0;
      root.style.removeProperty('--yc-fold');
      return;
    }
    if (bar && dot) {
      var fold = Math.round(bar.getBoundingClientRect().bottom - dot.getBoundingClientRect().bottom - 6);
      if (fold > 0) bandFold = fold;
    }
    if (!bandFold) return;
    root.style.setProperty('--yc-fold', bandFold + 'px');
    updateBand();
  }

  // --yc-p goes on the band itself (only its clip and its names use it), so scrolling restyles the band, not the
  // whole cart.
  function updateBand() {
    var area = root && root.querySelector('[data-rebuy-cart-scroll-area]');
    var bar = root && root.querySelector('[data-rebuy-component="progress-bar"]');
    if (!area || !bar || !bandFold) return;
    var p = Math.min(1, Math.max(0, area.scrollTop / bandFold));
    var value = p.toFixed(3);
    if (bar.style.getPropertyValue('--yc-p') !== value) bar.style.setProperty('--yc-p', value);
  }

  // Scroll events don't bubble; the capture phase catches the cart's list.
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.matches && e.target.matches('#rebuy-cart [data-rebuy-cart-scroll-area]')) updateBand();
  }, { capture: true, passive: true });

  // ---------- Multi-buy deals (USO, Ultimate Special Offers) ----------
  // USO applies its multi-buy deals ("2 for $80") only at checkout, through a draft order, so Shopify's cart and
  // Rebuy never see them. The cart asks USO's server what USO's own script asks (window.saso, with the cart as it is
  // now): the reply gives each deal reached and what it saves. Whether an item is in a deal at all comes from asking
  // the same about a pretend cart of two of it; the real cart is never changed. Totals, the progress bar and the gift
  // boxes then count the deal, as checkout will (the gift guard does the same). No answer from USO: Shopify's totals.

  var uso = cfg.uso || {};
  var deal = { cents: 0, bundles: [] }; // the latest reply: { cents saved, bundles: [{ names, orig, save }] }
  var dealAsked = null;                 // the cart last sent to USO
  var dealSeq = 0;
  var dealTimer = null;
  var dealTypes = {};                   // 'product_id:price' -> undefined while asking, null if none, else { count, price }
  var DEAL_SKIP = ['page_actions_data', 'discounts', 'action_crosssell_el', 'action_crosssell_popup_action', 'action_crosssell_popup_html'];

  // USO's checkout (sasoCheckout) must be on the page too: without it checkout goes to Shopify's own, with no deal.
  function usoOn() {
    return !!(uso.endpoint && window.saso && typeof window.saso === 'object' && typeof window.sasoCheckout === 'function' && isAud());
  }

  // USO has answered for exactly the cart on screen (Rebuy can show the cart from just before a change for a moment).
  function dealReady() {
    var cart = shopifyCart();
    return usoOn() && !!cart && cart.item_count > 0 && !!deal.sig && deal.sig === cartSig(cart);
  }

  function dealCents() {
    return dealReady() && deal.cents > 0 ? deal.cents : 0;
  }

  function dealName(count, priceCents) {
    return fill(uso.dealName || '{count} for {price}', { count: count, price: money(priceCents / 100) });
  }

  // USO's own request (its script sends window.saso less a few page-only keys), with this cart.
  function usoAsk(cart) {
    var body = {};
    Object.keys(window.saso).forEach(function (k) { if (DEAL_SKIP.indexOf(k) < 0) body[k] = window.saso[k]; });
    body.cart = cart;
    body.page_type = 'cart';
    return timed(uso.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=utf-8' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }).then(function (json) {
      var d = json && json.discounts;
      var amount = d ? parseFloat(d.total_discount_amount) : NaN;
      if (!isFinite(amount) || amount < 0) throw new Error('no deal amount');
      var after = parseFloat(d.discounted_price);
      var before = d.cart ? parseFloat(d.cart.original_total_price) : NaN;
      // The reply must add up (total before, less the saving, is the total after), or it is not trusted.
      if (amount > 0 && isFinite(before) && isFinite(after) && Math.abs(before - after - amount) > 0.05) throw new Error('deal does not add up');
      return { cents: Math.round(amount * 100), after: isFinite(after) ? Math.round(after * 100) : null, bundles: parseBundles(d) };
    });
  }

  // USO's note has one line per deal reached: "Name, Name $orig -$save", names cut to 40 characters.
  function parseBundles(d) {
    var note = d && d.cart && typeof d.cart.summary_note === 'string' ? d.cart.summary_note : '';
    var out = [];
    note.split('\n').forEach(function (line) {
      var m = line.trim().match(/^(.+?)\s+\$([\d,]+(?:\.\d+)?)\s+-\$([\d,]+(?:\.\d+)?)$/);
      if (!m) return;
      var orig = Math.round(parseFloat(m[2].replace(/,/g, '')) * 100);
      var save = Math.round(parseFloat(m[3].replace(/,/g, '')) * 100);
      if (save > 0 && orig > save) out.push({ raw: m[1], names: m[1].split(', '), orig: orig, save: save });
    });
    return out;
  }

  // USO separates the items in a deal with ", ", and some product names contain ", " themselves ("Here, Always,
  // Forever…"), so the names are read back against the cart's own titles: at each point, the longest stretch that
  // is the start of a cart title and ends where the list goes on (", ") or stops. That holds however USO shortens
  // the titles. A stretch no title explains falls back to the next ", ". Kept once a title has matched.
  function resolveNames(b, items) {
    if (b.resolved || !b.raw) return;
    var raw = titleKey(b.raw);
    var titles = [];
    items.forEach(function (i) {
      if (isGift(i)) return;
      var t = titleKey(i.product_title);
      if (t && titles.indexOf(t) < 0) titles.push(t);
    });
    var names = [], pos = 0, steps = 0, matched = false;
    while (pos < raw.length && steps++ < 40) {
      var rest = raw.slice(pos);
      var best = 0;
      titles.forEach(function (t) {
        var n = 0;
        while (n < t.length && n < rest.length && t.charAt(n) === rest.charAt(n)) n++;
        if (n > best && (n === rest.length || rest.substr(n, 2) === ', ')) best = n;
      });
      var hit;
      if (best > 0) { hit = rest.slice(0, best); matched = true; }
      else { var end = rest.indexOf(', '); hit = end > -1 ? rest.slice(0, end) : rest; }
      if (!hit) break;
      names.push(hit);
      pos += hit.length;
      if (raw.substr(pos, 2) === ', ') pos += 2;
      else if (pos < raw.length) { var next = raw.indexOf(', ', pos); pos = next > -1 ? next + 2 : raw.length; }
    }
    if (matched && names.length) { b.names = names; b.resolved = true; }
  }

  function cartSig(cart) {
    return cart && cart.items ? cart.total_price + '|' + cart.items.map(function (i) { return i.key + ':' + i.quantity; }).sort().join(',') : '';
  }

  // The part of the cart a deal can depend on: the paid lines that are in a deal, or not yet asked about. Lines
  // known to be in no deal (wrapping paper, a gift) are left out, so adding one of those keeps the deal on screen
  // while USO confirms it, instead of the Total jumping to the pre-deal price for a moment.
  function paidLines(cart) {
    return (cart && cart.items || []).filter(function (i) { return i.final_price > 0 && !isGiftCopy(i); })
      .map(function (i) { return { product_id: i.product_id, final_price: i.final_price, quantity: i.quantity, title: titleKey(i.product_title) }; });
  }

  // A line that could matter to a deal: in a deal on its own (or not yet asked), or named in the current answer
  // (a bundle of different items would be, while two of one of them earn nothing). Judged with what is known now.
  function dealRelevant(line) {
    if (dealTypes[line.product_id + ':' + line.final_price] !== null) return true;
    return deal.bundles.some(function (b) {
      return b.names.some(function (n) { var k = titleKey(n); return !!k && line.title.indexOf(k) === 0; });
    });
  }

  function dealKey(lines) {
    return (lines || []).filter(dealRelevant)
      .map(function (i) { return i.product_id + ':' + i.final_price + ':' + i.quantity; }).sort().join(',');
  }

  // Asks USO about the cart a moment after it stops changing; the reply redraws the cart.
  function watchDeal() {
    if (!usoOn()) return;
    var cart = shopifyCart();
    var sig = cartSig(cart);
    if (!sig || sig === dealAsked) return;
    dealAsked = sig;
    var seq = ++dealSeq;
    clearTimeout(dealTimer);
    if (!cart.items.length) { deal = { cents: 0, bundles: [] }; return; }
    // Nothing a deal depends on has changed: keep the current answer while the new one is on its way.
    if (deal.sig && deal.lines && dealKey(deal.lines) === dealKey(paidLines(cart))) deal.sig = sig;
    dealTimer = setTimeout(function () {
      var asked = '', lines = null;
      readCart()
        .then(function (fresh) { asked = cartSig(fresh); lines = paidLines(fresh); askTypes(fresh); return usoAsk(fresh); })
        .then(function (reply) { if (seq === dealSeq) { reply.sig = asked; reply.lines = lines; deal = reply; schedule(); } },
          // No answer: this cart counts as having no deal (so nothing waits on USO any longer).
          function () { if (seq === dealSeq) { deal = { cents: 0, bundles: [], sig: asked || sig, lines: lines }; schedule(); } });
    }, 250);
  }

  // Is each item in a deal? Asked once per product and price, about a pretend cart holding two of it.
  function askTypes(cart) {
    (cart.items || []).forEach(function (item) {
      if (!(item.final_price > 0) || !(item.quantity > 0) || isGiftCopy(item)) return;
      var key = item.product_id + ':' + item.final_price;
      if (key in dealTypes) return;
      dealTypes[key] = undefined;
      var two = {};
      Object.keys(item).forEach(function (k) { two[k] = item[k]; });
      two.quantity = 2;
      ['line_price', 'final_line_price', 'original_line_price'].forEach(function (k) {
        if (typeof item[k] === 'number') two[k] = Math.round(item[k] / item.quantity * 2);
      });
      two.line_level_discount_allocations = [];
      two.line_level_total_discount = 0;
      two.total_discount = 0;
      two.discounts = [];
      var pretend = {};
      Object.keys(cart).forEach(function (k) { pretend[k] = cart[k]; });
      pretend.items = [two];
      pretend.item_count = 2;
      pretend.total_price = pretend.items_subtotal_price = pretend.original_total_price = two.final_line_price;
      pretend.total_discount = 0;
      pretend.cart_level_discount_applications = [];
      usoAsk(pretend).then(function (reply) {
        dealTypes[key] = reply.cents > 0 && reply.after > 0 ? { count: 2, price: reply.after } : null;
        schedule();
      }, function () { delete dealTypes[key]; });
    });
  }

  function titleKey(text) {
    var t = document.createElement('textarea');
    t.innerHTML = String(text || '');
    return t.value.toLowerCase().replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
      .replace(/(?:\.\.\.|…)$/, '').replace(/\s+/g, ' ').trim();
  }

  // A title as USO shortens it in its note: at most 40 characters, cut back to a whole word.
  function uso40(title) {
    if (title.length <= 40) return title;
    var cut = title.slice(0, 41);
    var space = cut.lastIndexOf(' ');
    return (space > 0 ? cut.slice(0, space) : title.slice(0, 40)).trim();
  }

  // Which lines each deal is made of (USO names the items, title cut short): { lines: { key: { units, bundles } },
  // members: [ [keys] per bundle ] }. A line whose shortened title is exactly the name comes first, so "X Polo" never
  // takes a unit of "X Polo Dress".
  // USO names products, not lines, so when one product is on several lines (sizes) the choice is ours: the dearest
  // first (as USO pairs), then a line already in this deal, then the line with most units left. So a line of 2 is
  // its own pair and "Add 1 more" sits on the line that looks like it needs a partner (Quentin, 20 Oct).
  function dealLines(items) {
    var left = {}, out = { lines: {}, members: [] };
    items.forEach(function (i) { left[i.key] = isGift(i) ? 0 : i.quantity; });
    deal.bundles.forEach(function (b) { resolveNames(b, items); });
    deal.bundles.forEach(function (b, bi) {
      out.members[bi] = [];
      b.names.forEach(function (name) {
        var n = titleKey(name);
        if (!n) return;
        var open = items.filter(function (i) { return left[i.key] > 0; });
        var pool = open.filter(function (i) { return uso40(titleKey(i.product_title)) === n; });
        if (!pool.length) pool = open.filter(function (i) { return titleKey(i.product_title).indexOf(n) === 0; });
        pool.sort(function (a, c) {
          if (a.final_price !== c.final_price) return c.final_price - a.final_price;
          var au = out.members[bi].indexOf(a.key) > -1 ? 1 : 0, cu = out.members[bi].indexOf(c.key) > -1 ? 1 : 0;
          if (au !== cu) return cu - au;
          if (left[a.key] !== left[c.key]) return left[c.key] - left[a.key];
          return items.indexOf(a) - items.indexOf(c);
        });
        var line = pool[0];
        if (!line) return;
        left[line.key]--;
        var o = out.lines[line.key] || (out.lines[line.key] = { units: 0, bundles: [] });
        o.units++;
        if (o.bundles.indexOf(bi) < 0) o.bundles.push(bi);
        out.members[bi].push(line.key);
      });
    });
    return out;
  }

  // The badges under a line's quantity: the deal it is in (with the saving), and a nudge for a unit without a partner.
  function dealBadges(item, found) {
    var parts = [];
    var mine = found.lines[item.key];
    if (mine && deal.cents > 0) {
      var first = deal.bundles[mine.bundles[0]];
      var save = mine.bundles.reduce(function (n, bi) { return n + deal.bundles[bi].save; }, 0);
      var own = mine.bundles.every(function (bi) { return found.members[bi].every(function (k) { return k === item.key; }); });
      parts.push({ kind: 'on', text: dealName(first.names.length, first.orig - first.save) + ' · ' +
        fill(own ? (uso.saveOwn || 'You save {amount}') : (uso.savePair || 'Pair saves {amount}'), { amount: cents(save) }) });
    }
    // Nudges only once USO has answered for this cart: until then a unit may already have a partner on another line.
    var type = dealReady() ? dealTypes[item.product_id + ':' + item.final_price] : null;
    var spare = item.quantity - (mine && deal.cents > 0 ? mine.units : 0);
    if (type && spare > 0 && spare % type.count) {
      var need = type.count - (spare % type.count);
      parts.push({ kind: 'nudge', text: fill(mine ? (uso.nudgeMore || 'Add {count} more for another {deal}') : (uso.nudge || '{deal} · Add {count} more'),
        { deal: dealName(type.count, type.price), count: need }) });
    }
    return parts;
  }

  function applyDeals() {
    watchDeal();
    var lis = root.querySelectorAll('[data-rebuy-component="cart-items"] li.rebuy-cart__flyout-item');
    var items = lines();
    if (!lis.length || lis.length !== items.length) return;
    var on = usoOn();
    var found = on && dealCents() ? dealLines(items) : { lines: {}, members: [] };
    for (var i = 0; i < lis.length; i++) {
      var item = items[i];
      if ((' ' + lis[i].className + ' ').indexOf(' product-' + item.handle + ' ') < 0) continue;
      var info = lis[i].querySelector('.rebuy-cart__flyout-item-info');
      if (!info) continue;
      var box = null;
      for (var c = 0; c < info.children.length; c++) { if (info.children[c].classList.contains('yc-deal')) box = info.children[c]; }
      var parts = on && !isGift(item) ? dealBadges(item, found) : [];
      if (!parts.length) { if (box) box.remove(); continue; }
      var key = JSON.stringify(parts);
      if (box && box.getAttribute('data-yc-deal') === key) continue;
      if (!box) { box = el('div', 'yc-deal'); info.appendChild(box); }
      box.setAttribute('data-yc-deal', key);
      box.textContent = '';
      parts.forEach(function (p) {
        box.appendChild(el('span', 'yc-deal__badge is-' + p.kind, p.text));
        // "Deal applied at checkout" in plain text under the deal itself.
        if (p.kind === 'on' && uso.applied) box.appendChild(el('p', 'yc-deal__note', uso.applied));
      });
    }
  }

  // The progress bar with a deal, drawn as Rebuy draws it (each tier owns an equal share of the track; the fill
  // reaches the middle of a tier's share when the tier is reached, and moves in proportion between). Gift tiers count
  // the total after the deal, as the gift guard does. Free shipping keeps the total before it: the shipping app
  // (Parcelify) prices the deal's draft order on the items' own prices (checked 20 Oct: $158.85 less a $33.90 deal
  // still ships free), so that is what checkout will do.
  function applyDealBar() {
    var comp = root.querySelector('[data-rebuy-component="progress-bar"]');
    if (!comp) return;
    var sc = smartCart();
    var list = tiers();
    var saved = dealCents();
    var base = null;
    try { base = sc.progressBarSubtotal(); } catch (e) {}
    var plain = list.length && list.every(function (t) { return t.unlock_condition_type === 'price' && t.minimum != null; });
    if (!saved || typeof base !== 'number' || !plain) { dropAttr(comp, 'data-yc-deal'); return; }
    var mins = list.map(function (t) {
      try { var m = sc.getTierMinimum(t); if (typeof m === 'number') return m; } catch (e) {}
      return Math.round(t.minimum * 100);
    });
    var after = Math.max(0, base - saved);
    var n = mins.length, seg = 100 / n;
    // The total each tier is judged on, and whether it is reached.
    var basis = list.map(function (t) { return t.type === 'shipping' ? base : after; });
    var done = mins.map(function (m, i) { return basis[i] >= m; });
    var reached = 0;
    while (reached < n && done[reached]) reached++;
    var pct;
    if (reached === n) pct = 100;
    else if (n === 1) pct = basis[0] / mins[0] * 100;
    else if (reached === 0) pct = basis[0] / mins[0] * (seg / 2);
    else pct = reached * seg - seg / 2 + Math.max(0, Math.min(1, (basis[reached] - mins[reached - 1]) / (mins[reached] - mins[reached - 1]))) * seg;
    setAttr(comp, 'data-yc-deal', '');
    comp.style.setProperty('--yc-deal-fill', Math.max(0, Math.min(pct, 100)).toFixed(3) + '%');
    var steps = root.querySelectorAll('.rebuy-cart__progress-step');
    for (var i = 0; i < steps.length && i < n; i++) setAttr(steps[i], 'data-yc-done', done[i] ? '1' : '0');
    // The message Rebuy would show for this total: what the next tier needs, or the last tier's "reached" wording.
    var lang = (sc.progressBar && sc.progressBar.language) || {};
    var next = reached < n ? list[reached] : null;
    var text = '';
    if (next) {
      text = next.type === 'shipping' ? lang.free_shipping_remaining
        : (next.decorative_amount_remaining_label_text || next.product_amount_remaining_label_text || next.discount_amount_remaining_label_text);
      text = String(text || '').replace(/\{\{remaining_amount\}\}/g, cents(mins[reached] - basis[reached]));
    } else {
      var last = list[n - 1];
      text = copy.allUnlocked || (last.type === 'shipping' ? lang.free_shipping_reached
        : (last.decorative_amount_reached_label_text || last.product_amount_reached_label_text || last.discount_amount_reached_label_text));
    }
    text = String(text || '').replace(/<[^>]*>/g, '').trim();
    var prompt = root.querySelector('.rebuy-cart__progress-bar-prompt');
    if (prompt) { if (text) setAttr(prompt, 'data-yc-deal-prompt', text); else dropAttr(prompt, 'data-yc-deal-prompt'); }
  }

  // A new line goes to the top of the items (Shopify lists the newest first), so the cart scrolls up to show it when
  // one appears: an add from "Popular right now", the recommendations or the size panel (Quentin, 20 Oct). Quantity
  // changes keep the line keys, so + and − leave the view where it is. Nothing on the first look after page load.
  var knownKeys = null;
  function applyNewLines() {
    var cart = shopifyCart();
    if (!cart || !Array.isArray(cart.items)) return;
    var keys = cart.items.map(function (i) { return i.key; });
    var added = knownKeys !== null && keys.some(function (k) { return knownKeys.indexOf(k) < 0; });
    knownKeys = keys;
    if (added) scrollCartTop();
  }

  // ---------- Cart lines ----------

  function applyLines() {
    var lis = root.querySelectorAll('[data-rebuy-component="cart-items"] li.rebuy-cart__flyout-item');
    var items = lines();
    // Only when Rebuy's list and its data line up; otherwise leave Rebuy's own line as it is.
    if (!lis.length || lis.length !== items.length) return;
    for (var i = 0; i < lis.length; i++) {
      var li = lis[i];
      var item = items[i];
      if ((' ' + li.className + ' ').indexOf(' product-' + item.handle + ' ') < 0) continue;
      var info = li.querySelector('.rebuy-cart__flyout-item-info');
      var price = li.querySelector('.rebuy-cart__flyout-item-price');
      var variant = li.querySelector('.rebuy-cart__flyout-item-variant-title');
      var qty = li.querySelector('.rebuy-cart__flyout-item-quantity');
      var meta = info && info.querySelector('.yc-gift-meta');
      setAttr(li, 'data-yc-qty', String(item.quantity));

      // data-yc-line: the line has its own way out (bin in the stepper, or Change), so the corner remove can go.
      // A gift copy that has a price has neither (the gift guard hides its stepper), so it keeps the corner remove.
      if (isGift(item)) {
        setAttr(li, 'data-yc-line', '');
        setAttr(li, 'data-yc-gift', '');
        trimGiftName(li.querySelector('.rebuy-cart__flyout-item-product-title'));
        setAttr(info, 'data-yc-pill', copy.giftPill || 'Free gift');
        setAttr(price, 'data-yc-free', copy.giftFree || 'Free');
        if (info && !meta) {
          meta = el('div', 'yc-gift-meta');
          meta.appendChild(el('span', 'yc-gift-meta__qty'));
          var change = el('button', 'yc-gift-change', copy.giftChange || 'Change');
          change.type = 'button';
          meta.appendChild(change);
          info.appendChild(meta);
        }
        if (meta) {
          setText(meta.firstChild, fill(copy.giftQty || 'Qty {qty}', { qty: item.quantity }));
          var btn = meta.querySelector('.yc-gift-change');
          setAttr(btn, 'data-yc-key', item.key);
          setAttr(btn, 'aria-label', (copy.giftChange || 'Change') + ' ' + giftName(item.product_title || item.title || ''));
        }
        dropAttr(qty, 'data-yc-total');
      } else {
        // Only lines with a working minus (Rebuy drops or disables it for some tags; the guard hides it on gift copies).
        var minus = qty && qty.querySelector('.rebuy-cart__flyout-item-quantity-widget-button');
        var canStep = !!minus && !minus.disabled && getComputedStyle(qty).display !== 'none';
        if (canStep && !isGiftCopy(item)) setAttr(li, 'data-yc-line', '');
        else dropAttr(li, 'data-yc-line');
        // From 2 up, Rebuy's own remove button comes back as a "Remove" link under the stepper (the bin covers 1).
        setAttr(li.querySelector('.rebuy-cart__flyout-item-remove'), 'data-yc-label', copy.remove || 'Remove');
        dropAttr(li, 'data-yc-gift');
        dropAttr(info, 'data-yc-pill');
        dropAttr(price, 'data-yc-free');
        if (meta) meta.remove();
        var opts = item.options_with_values || [];
        if (variant && opts.length === 1 && /^size$/i.test(opts[0].name || '')) setAttr(variant, 'data-yc-prefix', copy.sizePrefix || 'Size ');
        else dropAttr(variant, 'data-yc-prefix');
        // "Edit" sits at the end of the size text and opens the size panel.
        var edit = variant && variant.querySelector('.yc-edit');
        if (variant && canEditSize(item)) {
          if (!edit) {
            edit = el('button', 'yc-edit', size.edit || 'Edit');
            edit.type = 'button';
            variant.appendChild(edit);
          }
          setAttr(edit, 'data-yc-key', item.key);
          var editTitle = editsAll(item.product_type, opts.length) ? (size.editOptionsTitle || 'Edit options') : (size.title || 'Edit size');
          setAttr(edit, 'aria-label', editTitle + ': ' + (item.product_title || ''));
        } else if (edit) {
          edit.remove();
        }
        if (typeof item.final_line_price === 'number') setAttr(qty, 'data-yc-total', cents(item.final_line_price));
      }
    }
  }

  // ---------- Gift boxes ----------

  var swap = {};       // widget id (or '*' for a gift with no box id) -> the customer tapped Change on that box's gift
  var leaving = {};    // line key -> gift taken out with Change, until it is gone from the cart
  var hadGift = {};    // widget id -> its gift was in the cart at the last pass
  var dropped = {};    // widget id -> the gift came out because the cart fell under the tier
  var wasChoosing = {}; // widget id -> its box had a gift to choose at the last pass

  // The cart's scrolling area back to the top (smoothly), e.g. so a gift box or a just-chosen gift is in view.
  // The browser's scroll anchoring would hold the view where it was when the box appears above it, so it is turned
  // off for the cart's scrolling area, and the scroll waits a moment for the box to be drawn.
  function scrollCartTop() {
    var body = root && root.querySelector('[data-rebuy-cart-anchor="body"]');
    var scroller = body && body.parentElement;
    if (!scroller) return;
    scroller.style.overflowAnchor = 'none';
    setTimeout(function () {
      var from = scroller.scrollTop;
      if (from > 0) {
        try { scroller.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { scroller.scrollTop = 0; }
      }
      setTimeout(function () {
        // Smooth scrolling only runs while the page is being drawn; if it hasn't moved at all, jump.
        if (from > 0 && scroller.scrollTop === from) scroller.scrollTop = 0;
        scroller.style.overflowAnchor = '';
      }, 700);
    }, 60);
  }
  var pools = {};      // widget id -> undefined while loading, null if it failed, else { threshold, operator, products, complete }
  var POOL_LIMIT = 50; // well above any gift rule's product count, so a full page never passes for a complete list

  function rebuyKey() {
    return (window.Rebuy && window.Rebuy.shop && window.Rebuy.shop.api_key) || (window.__giftGuard && window.__giftGuard.rebuyKey) || '';
  }

  // The gifts a box offers and its spend rule, from the same Rebuy data source the box uses.
  // Read once per page; a failure is not retried, and the locked box simply does not show.
  function knownPool(widgetId) {
    if (!(widgetId in pools)) {
      pools[widgetId] = undefined;
      loadPool(widgetId).then(function (pool) { pools[widgetId] = pool; schedule(); }, function () { pools[widgetId] = null; });
    }
    return pools[widgetId] || null;
  }

  // Same rule Rebuy applies to the box ("greater than $100").
  function earned(pool, spend) {
    return pool.operator === 'greater_than' ? spend > pool.threshold : spend >= pool.threshold;
  }

  function loadPool(widgetId) {
    var api = 'https://rebuyengine.com/api/v1';
    var key = encodeURIComponent(rebuyKey());
    return fetch(api + '/widgets/settings?id=' + encodeURIComponent(widgetId) + '&key=' + key)
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(function (json) {
        var ep = json && json.data && json.data.endpoint;
        if (typeof ep !== 'string' || !/^\/custom\/id\/\d+$/.test(ep)) throw new Error('no endpoint');
        // A very large cart subtotal so the rule matches and the gift list comes back.
        return fetch(api + ep + '?key=' + key + '&cart_subtotal=100000000&limit=' + POOL_LIMIT);
      })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(function (json) {
        var rule = null;
        ((json.metadata && json.metadata.matched_rules) || []).forEach(function (group) {
          (group.logic || []).forEach(function (logic) {
            (logic.rules || []).forEach(function (r) {
              if (r.type === 'cart_subtotal' && !isNaN(parseFloat(r.value)) && !rule) rule = r;
            });
          });
        });
        if (!rule) throw new Error('no subtotal rule');
        var products = (json.data || []).filter(function (p) { return p && p.title; });
        return {
          threshold: Math.round(parseFloat(rule.value) * 100),
          operator: rule.operator,
          products: products,
          // A full page may have left gifts off the end, so it can't say which gifts are sold out.
          complete: (json.data || []).length < POOL_LIMIT
        };
      });
  }

  // What a gift is worth, for its "value" line: the compare-at price, else the price (a gift copy that carries a
  // price is still free through Rebuy's 100% discount). Not Rebuy's own crossed-out amount: the 2nd box discounts
  // "from original price", which is $0.00 on a gift copy.
  function giftValueCents(product) {
    var v = product && product.variants && product.variants[0];
    if (!v) return 0;
    var compare = parseFloat(v.compare_at_price) || 0;
    var price = parseFloat(v.price) || 0;
    return Math.round((compare > 0 ? compare : price) * 100);
  }

  function lockedTile(product) {
    var tile = el('div', 'yc-gift-tile');
    var media = el('div', 'yc-gift-tile__media');
    var src = product.image && product.image.src;
    if (src) {
      var img = el('img');
      img.src = src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=242';
      img.alt = '';
      img.loading = 'lazy';
      img.width = 121;
      img.height = 150;
      media.appendChild(img);
    }
    var lock = el('span', 'yc-gift-tile__lock');
    if (assets.lock) {
      // Natural size from the SVG's own width and height (Figma padlock, 12 x 16).
      var icon = el('img');
      icon.src = assets.lock;
      icon.alt = '';
      lock.appendChild(icon);
    }
    media.appendChild(lock);
    tile.appendChild(media);
    tile.appendChild(el('p', 'yc-gift-tile__title', giftName(product.title)));
    var value = giftValueCents(product);
    var line = el('p', 'yc-gift-tile__price');
    line.appendChild(el('span', 'yc-gift-tile__free', copy.giftFree || 'Free'));
    if (value > 0) line.appendChild(el('span', 'yc-gift-tile__value', cents(value) + (copy.giftValue || ' value')));
    tile.appendChild(line);
    return tile;
  }

  function lockedBox(comp, gift, pool, spend, byDeal) {
    var box = comp.querySelector('.yc-gift-locked');
    if (!box) {
      box = el('section', 'yc-gift-locked');
      var head = el('div', 'yc-gift-locked__head');
      head.appendChild(el('p', 'yc-gift-locked__title'));
      head.appendChild(el('p', 'yc-gift-locked__sub'));
      box.appendChild(head);
      var row = el('div', 'yc-gift-locked__tiles');
      pool.products.forEach(function (p) { row.appendChild(lockedTile(p)); });
      box.appendChild(row);
      comp.insertBefore(box, comp.firstChild);
    }
    var threshold = money(pool.threshold / 100);
    // Never "$0.00 more": at exactly the threshold a "greater than" rule still needs a cent.
    var togo = cents(Math.max(pool.threshold - spend, 1));
    var droppedCopy = byDeal && gift.droppedDeal ? gift.droppedDeal : gift.dropped;
    var sub = dropped[gift.widget] ? fill(droppedCopy, { threshold: threshold, X: togo }) : fill(copy.lockedSub, { X: togo });
    setText(box.querySelector('.yc-gift-locked__title'), fill(gift.locked, { threshold: threshold }));
    setText(box.querySelector('.yc-gift-locked__sub'), sub);
    box.hidden = false;
  }

  function applyGifts() {
    var gifts = cfg.gifts || [];
    if (!gifts.length) return;
    // Same total Rebuy unlocks the boxes on, so the box, its amount to go and the bar's message agree; less any
    // multi-buy deal, which checkout takes off (the gift guard judges gifts the same way).
    var spend = isAud() ? rebuySubtotal() : null;
    var listSpend = spend;
    var dealOff = dealCents();
    if (spend !== null && dealOff) spend = Math.max(0, spend - dealOff);
    var all = lines();
    // USO not answered yet for this cart (a moment after each change): a held box stays held meanwhile.
    var dealWaiting = usoOn() && !dealReady() && all.length > 0;
    // Likely: a deal applied before, or a paid line that is in a deal or not yet asked about (the first look).
    var dealLikely = dealWaiting && (deal.cents > 0 || all.some(function (l) {
      return l.final_price > 0 && !isGiftCopy(l) && dealTypes[l.product_id + ':' + l.final_price] !== null;
    }));
    // A gift on its way out after Change no longer counts as claimed, so the swap wording holds while it goes.
    Object.keys(leaving).forEach(function (key) {
      if (!all.some(function (l) { return l.key === key; })) delete leaving[key];
    });
    var giftLines = all.filter(function (l) { return isGift(l) && !leaving[l.key]; });
    if (swap['*'] && giftLines.length) delete swap['*'];

    gifts.forEach(function (gift) {
      var mount = root.querySelector('[data-rebuy-component="cross-sell-widget"] [data-rebuy-id="' + gift.widget + '"]');
      var comp = mount && mount.closest('[data-rebuy-component="cross-sell-widget"]');
      if (!comp) return;
      var widget = mount.querySelector('#rebuy-widget-' + gift.widget);
      var claimed = giftLines.some(function (l) { return giftWidgetOf(l) === gift.widget; });
      var open = !!(widget && widget.querySelector('.rebuy-product-block'));
      // The gift list is only fetched once the cart has something in it.
      var pool = all.length ? knownPool(gift.widget) : null;
      var reached = pool && spend !== null ? earned(pool, spend) : null;

      var hadGiftBefore = hadGift[gift.widget];
      // The gift came out with the cart now under the tier (Rebuy's box script removes it). Judged on the spend,
      // not on the box, because Rebuy redraws the box a moment later.
      if (hadGift[gift.widget] && !claimed && reached === false) dropped[gift.widget] = true;
      if (claimed || reached === true || !all.length) dropped[gift.widget] = false;
      hadGift[gift.widget] = claimed;
      if (claimed || reached === false) delete swap[gift.widget];

      // Rebuy's own box: Daniel's heading, or the swap wording after Change.
      var heading = widget && widget.querySelector('.primary-title');
      if (heading) {
        var swapping = swap[gift.widget] || swap['*'];
        setAttr(heading, 'data-yc-title', swapping ? copy.swapTitle : gift.unlocked);
        setAttr(heading, 'data-yc-sub', swapping ? copy.swapSub : gift.unlockedSub);
      }
      if (widget) {
        // Rebuy's box can offer a gift its stock data still counts as in stock after Shopify sold out of it (the
        // black face mask, 19 Oct: Shopify -4, Rebuy 1), and Shopify then refuses the add. The gift list above
        // leaves sold-out gifts out, so the box hides any gift that list doesn't have, and the two always match.
        var sellable = pool && pool.complete ? pool.products.map(function (p) { return String(p.id); }) : null;
        var known = {};
        if (pool) pool.products.forEach(function (p) { known[String(p.id)] = p; });
        var blocks = widget.querySelectorAll('.rebuy-product-block');
        var ids = [];
        for (var i = 0; i < blocks.length; i++) ids.push((blocks[i].className.match(/\bproduct-id-(\d+)\b/) || [])[1]);
        // Never an empty box: if the list matches none of the box's gifts, it is the list that's off.
        if (sellable && !ids.some(function (id) { return id && sellable.indexOf(id) > -1; })) sellable = null;
        for (var b = 0; b < blocks.length; b++) {
          var pid = ids[b];
          if (sellable && pid && sellable.indexOf(pid) === -1) setAttr(blocks[b], 'data-yc-soldout', '');
          else dropAttr(blocks[b], 'data-yc-soldout');
          trimGiftName(blocks[b].querySelector('.rebuy-product-title'));
          setAttr(blocks[b].querySelector('.rebuy-money.sale'), 'data-yc-free', copy.giftFree || 'Free');
          // Value line: from the gift list when it has the gift, else Rebuy's amount; none when that is $0.00.
          var compareAt = blocks[b].querySelector('.rebuy-money.compare-at');
          if (compareAt) {
            var shown = compareAt.querySelector(':scope > :not(.sr-only)');
            var worth = known[pid] ? giftValueCents(known[pid]) : Math.round((parseFloat((shown ? shown.textContent : '').replace(/[^0-9.]/g, '')) || 0) * 100);
            setAttr(compareAt, 'data-yc-value', worth > 0 ? cents(worth) + (copy.giftValue || ' value') : '');
          }
        }
      }

      // Rebuy opens its box on the total before the deal; with the deal counted the tier may not be reached, so
      // its box stays closed and the locked box (with the amount still to spend) shows instead. While USO's answer
      // is on its way and a deal is likely (one applied before, or a deal item is in the cart), a box that has no
      // gift yet stays closed too, so a gift can't be taken in the gap and then come out again.
      // A box already showing stays while USO answers: closing it for that moment and opening it again pulled the
      // items up and back and sent the cart to the top on every + or − (Kurt, 22 Oct). If the deal does take the cart
      // under the tier, the first rule closes it once the answer is in.
      var held = !!(widget && open && reached === false && dealOff);
      if (widget && open && !claimed && dealLikely && !wasChoosing[gift.widget]) held = true;
      if (widget && dealWaiting && widget.hasAttribute('data-yc-held')) held = true;
      if (held) setAttr(widget, 'data-yc-held', ''); else if (widget) dropAttr(widget, 'data-yc-held');
      if (held) open = false;
      // That box stays in view while USO answers, but its gifts can't be taken until the answer is in: the deal may
      // put the cart under the tier, and a gift taken in that gap would only come out again.
      var busy = !!(widget && open && !claimed && dealLikely && wasChoosing[gift.widget]);
      if (busy) setAttr(widget, 'data-yc-busy', ''); else if (widget) dropAttr(widget, 'data-yc-busy');

      // A box with a gift to choose sits above the items (Quentin, 20 Oct), so it is seen; the cart scrolls up when
      // one opens, and again when a gift is chosen (the new gift line is at the top of the items).
      // Only a box Rebuy is actually showing. While USO's answer for a changed cart is on its way, a box can't newly
      // move up (the deal may close it again a moment later).
      var choosing = open && !claimed && !!widget && widget.offsetParent !== null;
      if (choosing && dealWaiting && !wasChoosing[gift.widget]) choosing = false;
      if (choosing) setAttr(comp, 'data-yc-gift-open', ''); else dropAttr(comp, 'data-yc-gift-open');
      if ((choosing && wasChoosing[gift.widget] === false) || (claimed && hadGiftBefore === false)) scrollCartTop();
      wasChoosing[gift.widget] = choosing;

      // Locked box only while Rebuy's own rule says the tier is not reached and its gift is not in the cart.
      var existing = comp.querySelector('.yc-gift-locked');
      if (claimed || open || reached !== false || !pool.products.length) {
        if (existing) existing.hidden = true;
        return;
      }
      // "With your deal applied…" only when the deal is what took the cart under the tier.
      lockedBox(comp, gift, pool, spend, !!(dealOff && listSpend !== null && earned(pool, listSpend)));
    });
  }

  // Change on a gift line: take the gift out, so its box opens again with the swap wording.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart .yc-gift-change') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var key = btn.getAttribute('data-yc-key');
    var item = lines().filter(function (l) { return l.key === key; })[0];
    if (!item || !isGift(item)) return;
    var widgetId = giftWidgetOf(item);
    swap[widgetId || '*'] = true;
    leaving[key] = true;
    btn.disabled = true;
    // If the gift is somehow still there a few seconds on, let the customer try again.
    setTimeout(function () {
      if (lines().some(function (l) { return l.key === key; })) { delete leaving[key]; btn.disabled = false; schedule(); }
    }, 5000);
    var removal;
    try {
      // The Smart Cart's own remove, so Rebuy also announces the change to screen readers. It silently does nothing
      // for a line still marked from a failed earlier attempt, so a retry goes through the plain cart remove.
      var sc = smartCart();
      removal = sc && typeof sc.removeItem === 'function' && item.status === 'ready' ? sc.removeItem(item) : window.Rebuy.Cart.removeItem(item);
    } catch (err) {
      removal = fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      }).then(function () { try { window.Rebuy.Cart.fetchCart(); } catch (e2) {} });
    }
    // Rebuy's box script shows the box again about half a second after the cart changes; bring it into view then.
    Promise.resolve(removal).then(function () {
      if (!widgetId) return;
      var tries = 0;
      (function reveal() {
        var box = root && root.querySelector('#rebuy-widget-' + widgetId);
        if (box && box.offsetParent && box.querySelector('.rebuy-product-block')) {
          if (box.scrollIntoView) box.scrollIntoView({ block: 'start' });
        } else if (++tries < 20) {
          setTimeout(reveal, 150);
        }
      })();
    });
  });

  // ---------- Edit size (Phase 3) ----------
  // Rebuy cannot change a line's variant, so a new size is added (same quantity and line details) and the old
  // line removed. Offered only on sized apparel with no personalisation or bundle details on the line.

  var size = cfg.size || {};
  // Line details that are safe to carry to the new size as they are (Rebuy's tracking and the pre-order note,
  // which is set per product, not per size). Any other detail means the line is left alone.
  var CARRIED = ['_source', '_attribution', '_widget_id', 'preorder', '_preorder'];
  var LOCKED_TAGS = ['PRODUCT_PersonalisedName', 'Personalised_NewRange', '_badge:Personalise', 'COLLECTION_Personalised Product', 'freegift'];

  function sizeIndex(names) {
    for (var i = 0; i < (names || []).length; i++) {
      var name = typeof names[i] === 'string' ? names[i] : names[i] && names[i].name;
      if (/^size$/i.test(name || '')) return i;
    }
    return -1;
  }

  function canEditSize(item) {
    if (!item || item.gift_card || item.product_has_only_default_variant || isGiftCopy(item)) return false;
    var type = item.product_type || '';
    if (!(size.types || []).some(function (t) { return type.indexOf(t) === 0; })) return false;
    if (sizeIndex(item.options_with_values) < 0) return false;
    var props = item.properties || {};
    if (Object.keys(props).some(function (k) { return props[k] !== '' && props[k] != null && CARRIED.indexOf(k) < 0; })) return false;
    var tags = item.product && item.product.tags;
    if (typeof tags === 'string') tags = tags.split(/,\s*/);
    // The two personalisation tags match as the product pages test them (contains), like the size view.
    if (Array.isArray(tags) && tags.some(function (t) {
      return LOCKED_TAGS.indexOf(t) > -1 || t.indexOf('PRODUCT_PersonalisedName') > -1 || t.indexOf('Personalised_NewRange') > -1;
    })) return false;
    return true;
  }

  // Products whose every option the panel changes (gift bags: Size and Amount), not only the size.
  function editsAll(type, optionCount) {
    return optionCount > 1 && (size.allOptions || []).some(function (t) { return (type || '').indexOf(t) === 0; });
  }

  // Two choices to a row for long ones (gift bag sizes with measurements), three for medium ones ("Super King").
  function gridEl(values) {
    var grid = el('div', 'yc-size__grid');
    var longest = values.reduce(function (n, v) { return Math.max(n, String(v || '').length); }, 0);
    if (longest > 12) grid.classList.add('yc-size__grid--wide');
    else if (longest > 8) grid.classList.add('yc-size__grid--mid');
    return grid;
  }

  var panel = null;   // the panel's elements, built once
  var editing = null; // { item, data, index, current, chosen, trigger }

  function joinSizes(list) {
    if (list.length < 2) return list.join('');
    return list.slice(0, -1).join(', ') + (size.and || ' and ') + list[list.length - 1];
  }

  function buildPanel() {
    var wrap = el('div', 'yc-size');
    wrap.hidden = true;
    var scrim = el('div', 'yc-size__scrim');
    var box = el('div', 'yc-size__panel');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'yc-size-title');
    box.tabIndex = -1;

    var grab = el('div', 'yc-size__grabber');
    grab.setAttribute('aria-hidden', 'true');
    var head = el('div', 'yc-size__head');
    var back = el('button', 'yc-size__back');
    back.type = 'button';
    back.setAttribute('aria-label', size.back || 'Back');
    var title = el('p', 'yc-size__title', size.title || 'Edit size');
    title.id = 'yc-size-title';
    var close = el('button', 'yc-size__close');
    close.type = 'button';
    close.setAttribute('aria-label', size.close || 'Close');
    head.appendChild(back);
    head.appendChild(title);
    head.appendChild(close);

    var body = el('div', 'yc-size__body');
    var foot = el('div', 'yc-size__foot');
    var update = el('button', 'yc-size__update', size.update || 'Update size');
    update.type = 'button';
    var cancel = el('button', 'yc-size__cancel', size.cancel || 'Cancel');
    cancel.type = 'button';
    foot.appendChild(update);
    foot.appendChild(cancel);

    box.appendChild(grab);
    box.appendChild(head);
    box.appendChild(body);
    box.appendChild(foot);
    wrap.appendChild(scrim);
    wrap.appendChild(box);
    document.body.appendChild(wrap);

    scrim.addEventListener('click', closePanel);
    close.addEventListener('click', closePanel);
    cancel.addEventListener('click', closePanel);
    back.addEventListener('click', function () {
      if (wrap.getAttribute('data-view') === 'guide') {
        renderSizes();
        var link = body.querySelector('.yc-size__guide-link');
        if (link) link.focus();
      } else {
        closePanel();
      }
    });
    update.addEventListener('click', applySize);
    wrap.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); closePanel(); return; }
      // Keep Tab inside the panel (Rebuy's own trap only covers the cart behind it).
      if (e.key !== 'Tab') return;
      var focusable = [].filter.call(box.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'), function (n) {
        return !n.disabled && n.offsetParent !== null;
      });
      if (!focusable.length) { e.preventDefault(); return; }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === box)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    return { wrap: wrap, box: box, title: title, body: body, update: update };
  }

  // mode 'edit': change a cart line's size. mode 'add': a product with choices from "Popular right now" or the
  // recommendations — pick a size (or each option) and add one; item is then a stand-in
  // ({ handle, product_title, url, image, quantity: 1, properties }) with no key or variant.
  function openPanel(item, trigger, mode) {
    if (!panel) panel = buildPanel();
    var adding = mode === 'add';
    editing = { item: item, trigger: trigger, data: null, mode: adding ? 'add' : 'edit' };
    // Desktop: the panel covers the cart exactly.
    var flyout = root && root.querySelector('.rebuy-cart__flyout');
    var width = flyout ? Math.round(flyout.getBoundingClientRect().width) : 0;
    panel.wrap.style.setProperty('--yc-size-width', (width > 0 ? width : 428) + 'px');
    panel.wrap.setAttribute('data-view', 'sizes');
    panel.body.textContent = '';
    panel.body.setAttribute('aria-busy', 'true');
    panel.update.disabled = true;
    // While loading, a title from the + that opened it ("Select … Options", "Choose options for …").
    var hint = adding && trigger && /options/i.test(trigger.getAttribute('aria-label') || '');
    var all = !adding && editsAll(item.product_type, (item.options_with_values || []).length);
    setText(panel.title, adding ? (hint ? (size.addOptionsTitle || 'Choose options') : (size.addTitle || 'Choose a size'))
      : (all ? (size.editOptionsTitle || 'Edit options') : (size.title || 'Edit size')));
    showPanel();
    panel.body.appendChild(el('p', 'yc-size__loading', all ? (size.loadingOptions || 'Loading…') : (size.loading || 'Loading sizes…')));
    // Lets the back-in-stock form open above the panel (see the CSS).
    document.documentElement.classList.add('yc-size-open');
    panel.box.focus();

    loadSizes(item)
      .then(function (data) {
        if (!editing || editing.item !== item) return;
        var index = sizeIndex(data.options);
        // Coming-soon products can't be added on their own page either.
        if (data.locked || data.comingsoon || (!adding && index < 0)) throw new Error('not editable');
        editing.data = data;
        editing.index = index;
        if (adding) {
          // A plain product page. Pre-orders only with the shipping-time note the product page would add to the line.
          if (!data.standardPage || !data.variants.length) throw new Error('product page');
          if (data.preorder) {
            if (!data.preorderProperty) throw new Error('product page');
            item.properties = item.properties || {};
            item.properties[data.preorderProperty.name] = data.preorderProperty.value;
          }
          editing.current = null;
          editing.chosen = null;
          // Size as the only option: the size list. Anything else (gift bags: Size and Amount): a row per option,
          // with an option that has only one choice already picked.
          editing.multi = !(data.options.length === 1 && index === 0);
          editing.picked = data.options.map(function (name, i) {
            var values = optionValues(data, i);
            return values.length === 1 ? values[0] : null;
          });
        } else {
          var current = (data.variants || []).filter(function (v) { return v.id === item.variant_id; })[0];
          if (!current) throw new Error('not editable');
          editing.current = current;
          editing.chosen = current;
          // Gift bags: a row per option, starting from the line's own choices.
          editing.multi = editsAll(item.product_type, data.options.length);
          if (editing.multi) editing.picked = current.options.slice();
        }
        renderSizes();
      })
      .catch(function (err) {
        if (!editing || editing.item !== item) return;
        // Add mode: the product page can do what the panel can't. Edit mode: the page can't change a cart line, so
        // the panel says what happened and stays closable.
        if (adding) { window.location.href = item.url || ('/products/' + item.handle); return; }
        panel.body.textContent = '';
        panel.body.removeAttribute('aria-busy');
        var notEditable = err && err.message === 'not editable';
        var msg = el('p', 'yc-size__error', notEditable
          ? ((all && size.notEditableOptions) || size.notEditable || "This item's size can't be changed here.")
          : ((all && size.loadErrorOptions) || size.loadError || "Sorry, we couldn't load the sizes. Please close this and try again."));
        msg.setAttribute('role', 'alert');
        panel.body.appendChild(msg);
        panel.update.disabled = true;
      });
  }

  // Sizes, stock and size guide for the line's product (templates/product.cart-size.liquid), fresh each time.
  function loadSizes(item) {
    return timed('/products/' + encodeURIComponent(item.handle) + '?view=cart-size', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (text) { return JSON.parse(text); });
  }

  // The panel slides in from the right (up from the bottom on phones) via .yc-size.is-open in the CSS.
  // hidden only goes back on once the slide-out has finished.
  var PANEL_SLIDE_MS = 300;
  var panelHideTimer = null;

  function showPanel() {
    clearTimeout(panelHideTimer);
    panel.wrap.hidden = false;
    // Lay out at the off-screen position first so the slide-in runs.
    void panel.wrap.offsetWidth;
    panel.wrap.classList.add('is-open');
  }

  function hidePanel() {
    panel.wrap.classList.remove('is-open');
    clearTimeout(panelHideTimer);
    panelHideTimer = setTimeout(function () { panel.wrap.hidden = true; }, PANEL_SLIDE_MS);
  }

  function closePanel() {
    if (!panel || !panel.wrap.classList.contains('is-open')) return;
    // Not while a size change is under way: its result (or error) must be seen.
    if (editing && editing.busy) return;
    hidePanel();
    document.documentElement.classList.remove('yc-size-open');
    var trigger = editing && editing.trigger;
    editing = null;
    // Back to the Edit button, or to the cart if Rebuy redrew that line.
    if (trigger && document.contains(trigger)) trigger.focus();
    else {
      var fallback = root && root.querySelector('.rebuy-cart__flyout-close');
      if (fallback) fallback.focus();
    }
  }

  // Sizes of this product that share the line's other options (colour and so on), in the product's own order.
  // In add mode the product has size as its only option, so every variant is a size.
  function sizeChoices() {
    var e = editing;
    if (!e.current) return e.data.variants.slice();
    return e.data.variants.filter(function (v) {
      return v.options.every(function (value, i) { return i === e.index || value === e.current.options[i]; });
    });
  }

  // The item at the top of the panel, with the shown variant's picture and price and, once picked, what was picked.
  function summaryEl(e, shown, picked) {
    var summary = el('div', 'yc-size__item');
    var src = shown.image || e.data.image || e.item.image;
    if (src) {
      var img = el('img', 'yc-size__image');
      img.src = src;
      img.alt = '';
      summary.appendChild(img);
    }
    var info = el('div', 'yc-size__info');
    info.appendChild(el('p', 'yc-size__name', e.item.product_title || e.data.title));
    var price = el('p', 'yc-size__price');
    if (shown.compare > shown.price) {
      price.appendChild(el('span', 'yc-size__price-sale', cents(shown.price)));
      price.appendChild(el('s', 'yc-size__price-was', cents(shown.compare)));
    } else {
      price.appendChild(el('span', 'yc-size__price-now', cents(shown.price)));
    }
    info.appendChild(price);
    if (picked) info.appendChild(el('p', 'yc-size__picked', picked));
    summary.appendChild(info);
    return summary;
  }

  function renderSizes() {
    var e = editing;
    if (!e || !e.data) return;
    if (e.multi) { renderOptions(); return; }
    var adding = e.mode === 'add';
    // Before a size is picked (add mode), the summary shows the first size in stock (or the first) for price and picture.
    var chosen = e.chosen;
    var shown = chosen || e.data.variants.filter(function (v) { return v.available; })[0] || e.data.variants[0];
    var sizeName = function (v) { return v ? v.options[e.index] : ''; };
    panel.wrap.setAttribute('data-view', 'sizes');
    setText(panel.title, adding ? (size.addTitle || 'Choose a size') : (size.title || 'Edit size'));
    var body = panel.body;
    body.textContent = '';
    body.removeAttribute('aria-busy');

    // The item, showing the chosen size's picture and price.
    body.appendChild(summaryEl(e, shown, chosen ? (copy.sizePrefix || 'Size ') + sizeName(chosen) : ''));

    var section = el('div', 'yc-size__sizes');
    var top = el('div', 'yc-size__row');
    var label = el('p', 'yc-size__label');
    label.appendChild(el('strong', null, size.label || 'Size'));
    label.appendChild(el('span', null, sizeName(chosen)));
    top.appendChild(label);
    if (e.data.guide) {
      var guide = el('button', 'yc-size__guide-link', size.guide || 'Size guide');
      guide.type = 'button';
      guide.addEventListener('click', renderGuide);
      top.appendChild(guide);
    }
    section.appendChild(top);

    var choices = sizeChoices();
    var grid = gridEl(choices.map(sizeName));
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', size.label || 'Size');
    var soldOut = [];
    choices.forEach(function (v) {
      var btn = el('button', 'yc-size__option', sizeName(v));
      btn.type = 'button';
      if (!v.available) {
        btn.disabled = true;
        btn.classList.add('is-soldout');
        btn.setAttribute('aria-label', fill(size.soldOutLabel, { size: sizeName(v) }));
        soldOut.push(v);
      } else {
        btn.setAttribute('aria-pressed', chosen && v.id === chosen.id ? 'true' : 'false');
        btn.addEventListener('click', function () {
          if (!editing || editing.busy || editing.added) return;
          editing.chosen = v;
          renderSizes();
          focusChosen();
        });
      }
      if (chosen && v.id === chosen.id) btn.classList.add('is-selected');
      grid.appendChild(btn);
    });
    section.appendChild(grid);

    // Enough of the new size for this line plus any of it already in the cart? Shopify would otherwise add part of
    // the quantity and refuse the rest.
    // Stock is capped at 50 in the view, so 50 means "50 or more". Skipped once the new size is in (retry removes only).
    var short = false;
    if (chosen) {
      var inCart = lines().reduce(function (n, l) { return l.variant_id === chosen.id && l.key !== e.item.key ? n + l.quantity : n; }, 0);
      var changing = adding || chosen.id !== e.current.id;
      short = !e.added && changing && chosen.stock !== null && chosen.stock < 50 && chosen.stock < e.item.quantity + inCart;
      if (chosen.available && chosen.stock !== null && chosen.stock > 0 && (short || chosen.stock <= (size.lowStockAt || 5))) {
        section.appendChild(el('p', 'yc-size__low', fill(size.lowStock, { count: chosen.stock, size: sizeName(chosen) })));
      }
    }
    if (soldOut.length) {
      var note = el('p', 'yc-size__soldout');
      var names = soldOut.map(sizeName);
      note.appendChild(document.createTextNode(fill(names.length > 1 ? size.soldOutMany : size.soldOutOne, { sizes: joinSizes(names) }) + ' '));
      note.appendChild(notifyLink(soldOut[0]));
      section.appendChild(note);
    }
    body.appendChild(section);

    // Update only for a different size with enough stock (add mode: once a size is picked); left alone while busy.
    if (e.busy) return;
    panel.update.disabled = !chosen || !chosen.available || short || (!adding && chosen.id === e.current.id);
    setText(panel.update, adding ? (size.add || 'Add to cart') : (size.update || 'Update size'));
  }

  // ---- Products with options other than a lone size (gift bags: Size and Amount), in add mode and on Edit ----

  // An option's values, in the order the product's variants give them.
  function optionValues(data, i) {
    var values = [];
    data.variants.forEach(function (v) { if (values.indexOf(v.options[i]) < 0) values.push(v.options[i]); });
    return values;
  }

  // Variants that agree with every pick, with option `i` (if given) set to `value` instead.
  function fitting(e, i, value) {
    return e.data.variants.filter(function (v) {
      return v.options.every(function (val, j) {
        if (j === i) return val === value;
        return e.picked[j] === null || val === e.picked[j];
      });
    });
  }

  function renderOptions() {
    var e = editing;
    var adding = e.mode === 'add';
    var names = e.data.options;
    var complete = e.picked.every(function (p) { return p !== null; });
    var chosen = e.chosen = complete ? fitting(e, -1)[0] || null : null;
    // Until everything is picked, the summary shows the first in-stock variant that fits the picks so far.
    var fits = fitting(e, -1);
    var shown = chosen || fits.filter(function (v) { return v.available; })[0] || fits[0] || e.data.variants[0];
    panel.wrap.setAttribute('data-view', 'sizes');
    setText(panel.title, adding ? (size.addOptionsTitle || 'Choose options') : (size.editOptionsTitle || 'Edit options'));
    var body = panel.body;
    body.textContent = '';
    body.removeAttribute('aria-busy');
    body.appendChild(summaryEl(e, shown, chosen ? chosen.options.join(' / ') : ''));

    names.forEach(function (name, i) {
      var section = el('div', 'yc-size__sizes');
      var top = el('div', 'yc-size__row');
      var label = el('p', 'yc-size__label');
      label.appendChild(el('strong', null, name));
      label.appendChild(el('span', null, e.picked[i] || ''));
      top.appendChild(label);
      if (i === e.index && e.data.guide) {
        var guide = el('button', 'yc-size__guide-link', size.guide || 'Size guide');
        guide.type = 'button';
        guide.addEventListener('click', renderGuide);
        top.appendChild(guide);
      }
      section.appendChild(top);

      var values = optionValues(e.data, i);
      var grid = gridEl(values);
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', name);
      var soldOut = [];
      values.forEach(function (value) {
        var withValue = e.data.variants.filter(function (v) { return v.options[i] === value; });
        var btn = el('button', 'yc-size__option', value);
        btn.type = 'button';
        // Off only when nothing in stock has this choice at all. One that's in stock but not alongside the other
        // picks stays on: picking it clears the picks it doesn't go with (below), so no combination is out of reach.
        if (!withValue.some(function (v) { return v.available; })) {
          btn.disabled = true;
          btn.classList.add('is-soldout');
          btn.setAttribute('aria-label', fill(size.soldOutLabel, { size: value }));
          var near = fitting(e, i, value)[0];
          soldOut.push({ value: value, variant: near || withValue[0] });
        } else {
          btn.setAttribute('aria-pressed', e.picked[i] === value ? 'true' : 'false');
          btn.addEventListener('click', function () {
            if (!editing || editing.busy || editing.added) return;
            // Keep each other pick only while something in stock still has it and all the picks kept so far.
            var next = editing.picked.map(function (p, j) { return j === i ? value : null; });
            editing.picked.forEach(function (p, j) {
              if (j === i || p === null) return;
              next[j] = p;
              var inStock = e.data.variants.some(function (v) {
                return v.available && v.options.every(function (val, k) { return next[k] === null || val === next[k]; });
              });
              if (!inStock) next[j] = null;
            });
            editing.picked = next;
            renderSizes();
            var again = panel.body.querySelectorAll('.yc-size__grid')[i];
            var picked = again && again.querySelector('.yc-size__option.is-selected');
            if (picked) picked.focus();
          });
        }
        if (e.picked[i] === value) btn.classList.add('is-selected');
        grid.appendChild(btn);
      });
      section.appendChild(grid);
      if (soldOut.length) {
        var note = el('p', 'yc-size__soldout');
        var list = soldOut.map(function (s) { return s.value; });
        // "Sizes 8 and 10 are sold out" only on a size option; other options get the plain wording.
        var text = i === e.index
          ? fill(list.length > 1 ? size.soldOutMany : size.soldOutOne, { sizes: joinSizes(list) })
          : fill(list.length > 1 ? (size.soldOutManyAny || '{values} are sold out.') : (size.soldOutOneAny || '{values} is sold out.'), { values: joinSizes(list) });
        note.appendChild(document.createTextNode(text + ' '));
        if (soldOut[0].variant) note.appendChild(notifyLink(soldOut[0].variant));
        section.appendChild(note);
      }
      body.appendChild(section);
    });

    // Enough stock for one more (Edit: the line's quantity) alongside any already in the cart (other lines on Edit),
    // as in renderSizes. Stock is capped at 50 in the view. Skipped once Edit's new choice is in (retry removes only).
    var short = false;
    if (chosen) {
      var inCart = lines().reduce(function (n, l) { return l.variant_id === chosen.id && (adding || l.key !== e.item.key) ? n + l.quantity : n; }, 0);
      var changing = adding || chosen.id !== e.current.id;
      short = !e.added && changing && chosen.stock !== null && chosen.stock < 50 && chosen.stock < (adding ? 1 : e.item.quantity) + inCart;
      if (chosen.available && chosen.stock !== null && chosen.stock > 0 && (short || chosen.stock <= (size.lowStockAt || 5))) {
        var last = body.lastChild;
        last.appendChild(el('p', 'yc-size__low', fill(size.lowStockAny || 'Only {count} left', { count: chosen.stock })));
      }
    }

    if (e.busy) return;
    panel.update.disabled = !chosen || !chosen.available || short || (!adding && chosen.id === e.current.id);
    setText(panel.update, adding ? (size.add || 'Add to cart') : (size.updateOptions || size.update || 'Update'));
  }

  // The back-in-stock app (AMP) opens its sign-up form for any element with class BIS_trigger and the product and
  // variant on it; it must be text-only for AMP to read them. Without AMP on the page, the product page instead.
  function notifyLink(variant) {
    var handle = editing.data.handle;
    if (window.BIS && typeof window.BIS.genericTriggerHandler === 'function') {
      var btn = el('button', 'yc-size__notify BIS_trigger', size.notify || 'Notify me');
      btn.type = 'button';
      btn.setAttribute('data-product-handle', handle);
      btn.setAttribute('data-variant-id', String(variant.id));
      // AMP's own page-wide click listener never hears clicks while the cart is open, so its handler is called here
      // (once: the click goes no further).
      btn.addEventListener('click', function (ev) {
        ev.stopPropagation();
        try { window.BIS.genericTriggerHandler.call(window.BIS, ev); } catch (err) { window.location.href = editing.data.url + '?variant=' + variant.id; }
      });
      return btn;
    }
    var a = el('a', 'yc-size__notify', size.notify || 'Notify me');
    a.href = editing.data.url + '?variant=' + variant.id;
    return a;
  }

  function focusChosen() {
    var btn = panel.body.querySelector('.yc-size__option.is-selected');
    if (btn) btn.focus();
  }

  // Size chart from the product's size chart page (shop content), shown in the panel with Back to return.
  function renderGuide() {
    var e = editing;
    // Not while a change is under way: its result must stay in view.
    if (!e || !e.data || !e.data.guide || e.busy) return;
    panel.wrap.setAttribute('data-view', 'guide');
    setText(panel.title, e.data.guide.title || size.guide || 'Size guide');
    panel.body.textContent = '';
    var guide = el('div', 'yc-size__guide');
    guide.innerHTML = e.data.guide.html;
    panel.body.appendChild(guide);
    panel.update.disabled = true;
    panel.box.focus();
  }

  // Cart requests give up after 15 s, so the panel can never sit on "Updating…" for good.
  function timed(url, options) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 15000) : null;
    if (ctrl) options.signal = ctrl.signal;
    return fetch(url, options).then(function (res) { clearTimeout(timer); return res; }, function (err) { clearTimeout(timer); throw err; });
  }

  function post(url, body) {
    return timed(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) { var err = new Error('HTTP ' + res.status); err.description = json && json.description; throw err; }
        return json;
      });
    });
  }

  function readCart() {
    return timed('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  }

  function details(props) {
    var out = {};
    Object.keys(props || {}).sort().forEach(function (k) {
      if (props[k] !== '' && props[k] != null) out[k] = String(props[k]);
    });
    return out;
  }

  // The line being changed in a fresh cart: by its key, or (if Rebuy re-keyed it) by its size and line details.
  function findLine(cart, item) {
    var want = JSON.stringify(details(item.properties));
    var byKey = cart.items.filter(function (l) { return l.key === item.key; })[0];
    return byKey || cart.items.filter(function (l) {
      return l.variant_id === item.variant_id && JSON.stringify(details(l.properties)) === want;
    })[0];
  }

  // A line is one size with one set of details (Shopify merges the rest), so that pair names it even if Rebuy re-keys it.
  function lineId(variantId, props) {
    return variantId + '|' + JSON.stringify(details(props));
  }

  // After a refused add, take back anything Shopify added anyway (it can add part of the quantity): the line with the
  // new size and this line's details goes back to its quantity before the add (0 if it is new). Resolves true if it did.
  function undoAdd(before, variantId, props) {
    var id = lineId(variantId, props);
    return readCart().then(function (cart) {
      var line = cart.items.filter(function (l) { return lineId(l.variant_id, l.properties) === id; })[0];
      var was = before[id] || 0;
      if (!line || line.quantity <= was) return false;
      return post('/cart/change.js', { id: line.key, quantity: was }).then(function () { return true; });
    });
  }

  // Add mode: one of the picked size goes into the cart (one unit, so Shopify either adds it or refuses).
  function addPicked(e) {
    var chosen = e.chosen;
    e.busy = true;
    panel.update.disabled = true;
    setText(panel.update, size.adding || 'Adding…');
    var old = panel.body.querySelector('.yc-size__error');
    if (old) old.remove();
    var countOf = function (cart) {
      return cart.items.reduce(function (n, l) { return l.variant_id === chosen.id ? n + l.quantity : n; }, 0);
    };
    var before = null; // that size's quantity in the cart just before the add
    var done = function () {
      e.busy = false;
      try { window.Rebuy.Cart.fetchCart(); } catch (err) {}
      // The + that opened the panel goes when the empty cart turns into a cart with items.
      e.trigger = null;
      if (editing === e) closePanel();
    };
    readCart()
      .then(function (cart) {
        before = countOf(cart);
        var line = { id: chosen.id, quantity: 1 };
        var props = details(e.item.properties);
        if (Object.keys(props).length) line.properties = props;
        return post('/cart/add.js', { items: [line] });
      })
      .then(done)
      .catch(function (err) {
        // A request that timed out may still have gone through: look before offering Add to cart again.
        var check = before === null ? Promise.resolve(false) : readCart().then(function (cart) { return countOf(cart) > before; }, function () { return false; });
        return check.then(function (added) {
          if (added) { done(); return; }
          e.busy = false;
          try { window.Rebuy.Cart.fetchCart(); } catch (err2) {}
          if (editing !== e) return;
          var msg = el('p', 'yc-size__error', (err && err.description) || size.addError || size.error);
          msg.setAttribute('role', 'alert');
          panel.body.appendChild(msg);
          setText(panel.update, size.add || 'Add to cart');
          panel.update.disabled = false;
        });
      });
  }

  function applySize() {
    var e = editing;
    if (!e || !e.data || e.busy || panel.update.disabled) return;
    if (e.mode === 'add') { addPicked(e); return; }
    var item = e.item;
    var chosen = e.chosen;
    e.busy = true;
    panel.update.disabled = true;
    setText(panel.update, size.updating || 'Updating…');
    var old = panel.body.querySelector('.yc-size__error');
    if (old) old.remove();
    var before = null;

    // 1. Add the new size (skipped on a retry once it is in). 2. Remove the old line, found afresh.
    var adding = e.added ? Promise.resolve() : readCart().then(function (cart) {
      before = {};
      cart.items.forEach(function (l) { before[lineId(l.variant_id, l.properties)] = l.quantity; });
      return post('/cart/add.js', { items: [{ id: chosen.id, quantity: item.quantity, properties: details(item.properties) }] });
    }).then(function () { e.added = true; });

    adding
      .then(readCart)
      .then(function (cart) {
        // Take off just the moved quantity (the old line keeps any extra Rebuy merged into it meanwhile).
        var line = findLine(cart, item);
        return line ? post('/cart/change.js', { id: line.key, quantity: Math.max(0, line.quantity - item.quantity) }) : null;
      })
      .then(function () {
        e.busy = false;
        try { window.Rebuy.Cart.fetchCart(); } catch (err) {}
        // The Edit button goes with the old line, so focus returns to the cart's close button.
        e.trigger = null;
        if (editing === e) closePanel();
      })
      .catch(function (err) {
        var undo = !e.added && before ? undoAdd(before, chosen.id, item.properties).catch(function () { return false; }) : Promise.resolve(false);
        return undo.then(function (undone) {
          // A refused add usually means the stock moved: reload the sizes so the stock check sees it.
          return e.added ? undone : loadSizes(e.item).then(function (data) {
            var fresh = data && data.variants.filter(function (v) { return v.id === chosen.id; })[0];
            var now = data && data.variants.filter(function (v) { return v.id === e.current.id; })[0];
            if (fresh && now && editing === e) { e.data = data; e.chosen = fresh; e.current = now; }
            return undone;
          }, function () { return undone; });
        }).then(function (undone) {
          e.busy = false;
          try { window.Rebuy.Cart.fetchCart(); } catch (err2) {}
          if (editing !== e) return;
          if (!e.added) renderSizes();
          // Shopify's wording when it refused the add outright; ours if we took a part-add back. Once the new size is
          // in but the old one didn't come out, say so (the cart shows both until the retry).
          // Gift bags get wording that isn't only about size.
          var msg = el('p', 'yc-size__error', e.added
            ? ((e.multi && size.errorRemoveOptions) || size.errorRemove || "Your new size is in the cart, but the old one couldn't be removed. Please try again.")
            : ((!undone && err && err.description) || (e.multi && size.errorOptions) || size.error));
          msg.setAttribute('role', 'alert');
          panel.body.appendChild(msg);
          // After a refused add, renderSizes has set Update from the fresh stock; after the add went in, Update retries.
          if (e.added) {
            setText(panel.update, e.multi ? (size.updateOptions || 'Update') : (size.update || 'Update size'));
            panel.update.disabled = false;
          }
        });
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart .yc-edit') : null;
    if (!btn) return;
    e.preventDefault();
    var key = btn.getAttribute('data-yc-key');
    var item = lines().filter(function (l) { return l.key === key; })[0];
    if (item && canEditSize(item)) openPanel(item, btn);
  });

  // + on a "Popular right now" product with choices: the size panel in add mode (the link itself is the product page,
  // used if the script isn't running or the product can't be added from the panel).
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('#rebuy-cart a.yc-pop__add[data-yc-handle]') : null;
    if (!link) return;
    e.preventDefault();
    var card = link.closest('.yc-pop__card');
    var img = card && card.querySelector('img');
    openPanel({
      handle: link.getAttribute('data-yc-handle'),
      product_title: link.getAttribute('data-yc-title') || '',
      url: link.getAttribute('href'),
      image: img ? img.src : null,
      quantity: 1,
      properties: {}
    }, link, 'add');
  });

  // ---------- Recommendations ("You may also like", "Gifting?") ----------

  function isGiftWidget(widget) {
    return (cfg.gifts || []).some(function (g) { return widget.id === 'rebuy-widget-' + g.widget; });
  }

  // A widget's products as Rebuy fetched them, by product id (for each tile's second picture).
  function widgetProducts(widget) {
    var all = window.Rebuy && window.Rebuy.widgets;
    var found = (Array.isArray(all) ? all : []).filter(function (x) { return x && 'rebuy-widget-' + x.id === widget.id; })[0];
    var byId = {};
    ((found && found.data && found.data.products) || []).forEach(function (p) { byId[String(p.id)] = p; });
    return byId;
  }

  // The product's second picture under the first, shown on hover as on the product cards (Kurt, 22 Oct). Only where
  // there is a mouse, so phones don't fetch pictures nobody sees.
  function applyRecAlt(block, media, products) {
    var pid = (block.className.match(/\bproduct-id-(\d+)\b/) || [])[1];
    var images = pid && products[pid] && products[pid].images;
    var src = images && images[1] && images[1].src;
    var alt = media.querySelector('.yc-rec-alt');
    if (!src) {
      if (alt) alt.remove();
      dropAttr(block, 'data-yc-alt');
      return;
    }
    src += (src.indexOf('?') > -1 ? '&' : '?') + 'width=500';
    if (!alt) {
      alt = el('img', 'yc-rec-alt');
      alt.alt = '';
      alt.loading = 'lazy';
      alt.decoding = 'async';
      media.appendChild(alt);
    }
    if (alt.getAttribute('src') !== src) alt.setAttribute('src', src);
    setAttr(block, 'data-yc-alt', '');
  }

  function applyRecs() {
    var hover = window.matchMedia('(hover: hover)').matches;
    var widgets = root.querySelectorAll('[data-rebuy-component="cross-sell-widget"] .rebuy-widget');
    for (var w = 0; w < widgets.length; w++) {
      if (isGiftWidget(widgets[w])) continue;
      var blocks = widgets[w].querySelectorAll('.rebuy-product-block');
      var products = hover && blocks.length ? widgetProducts(widgets[w]) : null;
      for (var b = 0; b < blocks.length; b++) {
        var media = blocks[b].querySelector('.rebuy-product-media');
        if (media && products) applyRecAlt(blocks[b], media, products);
        if (blocks[b].querySelector('.rebuy-money.compare-at')) setAttr(media, 'data-yc-badge', copy.saleBadge || 'Sale');
        else dropAttr(media, 'data-yc-badge');
        // A product with choices to make (clothing sizes, gift bag size and amount): + opens the size panel instead
        // of Rebuy's picker. Rebuy shows either dropdowns or a "Size" / "Options" button ("Select … size",
        // "Select … Options") that opens its own; single items show "Add" and still add straight away.
        var options = blocks[b].querySelector('.rebuy-product-options');
        var choices = options ? [].some.call(options.querySelectorAll('select'), function (s) { return s.options.length > 1; }) : false;
        var action = blocks[b].querySelector('.rebuy-product-actions button');
        var label = action ? action.getAttribute('aria-label') || '' : '';
        if (choices || /^Select .+ (size|options)$/i.test(label)) setAttr(blocks[b], 'data-yc-options', '');
        else dropAttr(blocks[b], 'data-yc-options');
      }
    }
  }

  // + on a recommended product with choices: the size panel in add mode, keeping Rebuy's tracking details on the line
  // (as its own Add would). Capture phase, so Rebuy's own picker or add never runs.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart .rebuy-product-block[data-yc-options] .rebuy-product-actions button') : null;
    // The end of a mouse drag along the row is not a click; the drag's own listener below cancels it.
    if (!btn || swallowClick) return;
    var block = btn.closest('.rebuy-product-block');
    var link = block.querySelector('a[href*="/products/"]');
    var handle = link ? (new URL(link.getAttribute('href'), window.location.origin).pathname.split('/products/')[1] || '').split('/')[0] : '';
    if (!handle) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var widget = block.closest('.rebuy-widget');
    var widgetId = widget && widget.id ? widget.id.replace('rebuy-widget-', '') : '';
    var title = block.querySelector('.rebuy-product-title');
    var img = block.querySelector('.rebuy-product-media img');
    var properties = { _source: 'Rebuy', _attribution: 'Rebuy Cart' };
    if (/^\d+$/.test(widgetId)) properties._widget_id = widgetId;
    openPanel({
      handle: handle,
      product_title: title ? title.textContent.trim() : '',
      url: '/products/' + handle,
      image: img ? img.src : null,
      quantity: 1,
      properties: properties
    }, btn, 'add');
  }, true);

  // The rows scroll natively; stop Rebuy's carousel from grabbing the drag.
  ['touchstart', 'mousedown'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (e.target && e.target.closest && e.target.closest('#rebuy-cart .rebuy-widget .splide__track')) e.stopPropagation();
    }, { capture: true, passive: true });
  });

  // ---------- Sideways rows on a computer ----------
  // Fingers and trackpads scroll the rows natively. A mouse can't, so: drag to scroll, and ‹ › beside the heading.

  var ROWS = '#rebuy-cart .rebuy-widget .splide__track, #rebuy-cart .yc-gift-locked__tiles';
  var drag = null;
  var swallowClick = false;

  document.addEventListener('pointerdown', function (e) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    var track = e.target && e.target.closest ? e.target.closest(ROWS) : null;
    if (!track || track.scrollWidth <= track.clientWidth) return;
    drag = { track: track, x: e.clientX, left: track.scrollLeft, moved: false };
  }, true);

  document.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 6) return;
    if (!drag.moved) { drag.moved = true; drag.track.classList.add('is-dragging'); }
    drag.track.scrollLeft = drag.left - dx;
    e.preventDefault();
  }, true);

  function endDrag() {
    if (!drag) return;
    // A drag is not a click: the product under the pointer is not opened or added.
    if (drag.moved) {
      swallowClick = true;
      setTimeout(function () { swallowClick = false; }, 300);
    }
    drag.track.classList.remove('is-dragging');
    drag = null;
  }
  document.addEventListener('pointerup', endDrag, true);
  document.addEventListener('pointercancel', endDrag, true);

  document.addEventListener('click', function (e) {
    if (!swallowClick) return;
    swallowClick = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  // ---------- Fingers: swipes and fling-stopping taps are not taps ----------
  // The cart and its rows are scroll areas. On iPhones a tap that stops a scroll still
  // fires a click on whatever is under the finger, so swiping through the cart kept
  // opening products. Drop clicks on a product (link, picture, + or Add) when the touch
  // moved, or started while the cart was still scrolling. Window capture runs before
  // Rebuy's handlers and the add handler above.
  var TOUCH_SLOP = 10;
  var SCROLL_SETTLE_MS = 200;
  var lastCartScroll = 0;
  var touch = null;

  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.closest && e.target.closest('#rebuy-cart')) lastCartScroll = Date.now();
  }, { capture: true, passive: true });

  document.addEventListener('touchstart', function (e) {
    var t = e.touches[0];
    touch = t ? { x: t.clientX, y: t.clientY, moved: false, duringScroll: Date.now() - lastCartScroll < SCROLL_SETTLE_MS } : null;
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', function (e) {
    var t = e.touches[0];
    if (!touch || !t) return;
    if (Math.abs(t.clientX - touch.x) > TOUCH_SLOP || Math.abs(t.clientY - touch.y) > TOUCH_SLOP) touch.moved = true;
  }, { capture: true, passive: true });

  window.addEventListener('click', function (e) {
    if (!touch || !(touch.moved || touch.duringScroll)) return;
    var block = e.target && e.target.closest ? e.target.closest('#rebuy-cart .rebuy-product-block, #rebuy-cart .yc-gift-tile, #rebuy-cart .yc-fill__card') : null;
    if (!block) return;
    touch = null;
    e.preventDefault();
    e.stopImmediatePropagation();
  }, true);

  // Rebuy focuses the close button when the cart opens; on a phone that shows as a box round the ✕.
  // Keyboard users keep the focus.
  var lastKey = 0;
  document.addEventListener('keydown', function () { lastKey = Date.now(); }, true);
  document.addEventListener('focusin', function (e) {
    if (!window.matchMedia('(hover: none)').matches || Date.now() - lastKey < 1000) return;
    if (e.target && e.target.matches && e.target.matches('#rebuy-cart .rebuy-cart__flyout-close')) e.target.blur();
  }, true);

  // Pictures and links would otherwise start the browser's own drag-and-drop.
  document.addEventListener('dragstart', function (e) {
    if (e.target && e.target.closest && e.target.closest(ROWS)) e.preventDefault();
  }, true);

  // ‹ › for each row that has more to show; greyed out at either end.
  function applyArrows() {
    var rows = [];
    var widgets = root.querySelectorAll('[data-rebuy-component="cross-sell-widget"] .rebuy-widget.is-visible .rebuy-widget-content');
    for (var i = 0; i < widgets.length; i++) rows.push({ box: widgets[i], track: widgets[i].querySelector('.splide__track') });
    var locked = root.querySelectorAll('.yc-gift-locked:not([hidden])');
    for (var j = 0; j < locked.length; j++) rows.push({ box: locked[j], track: locked[j].querySelector('.yc-gift-locked__tiles') });

    rows.forEach(function (row) {
      if (!row.track) return;
      var arrows = row.box.querySelector(':scope > .yc-arrows');
      var overflow = row.track.scrollWidth - row.track.clientWidth > 4;
      if (!overflow) { if (arrows) arrows.remove(); return; }
      if (!arrows) {
        arrows = el('div', 'yc-arrows');
        ['prev', 'next'].forEach(function (dir) {
          var b = el('button', 'yc-arrows__' + dir);
          b.type = 'button';
          b.setAttribute('aria-label', dir === 'prev' ? (copy.scrollBack || 'Scroll back') : (copy.scrollOn || 'Scroll on'));
          arrows.appendChild(b);
        });
        row.box.appendChild(arrows);
      }
      updateArrows(arrows, row.track);
    });
  }

  function updateArrows(arrows, track) {
    var max = track.scrollWidth - track.clientWidth;
    var prev = arrows.querySelector('.yc-arrows__prev');
    var next = arrows.querySelector('.yc-arrows__next');
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max - 2;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart .yc-arrows button') : null;
    if (!btn) return;
    var track = btn.closest('.rebuy-widget-content, .yc-gift-locked').querySelector('.splide__track, .yc-gift-locked__tiles');
    if (!track) return;
    // A page at a time: as many whole tiles as fit, so the next lands where the first one was.
    var tile = track.querySelector('.rebuy-product-block, .yc-gift-tile');
    var list = tile && tile.parentElement;
    var pitch = tile ? tile.getBoundingClientRect().width + (parseFloat(getComputedStyle(list).columnGap) || 0) : 0;
    var pad = parseFloat(getComputedStyle(track).paddingLeft) + parseFloat(getComputedStyle(track).paddingRight);
    var page = pitch ? Math.max(1, Math.floor((track.clientWidth - pad + pitch - 2) / pitch)) * pitch : track.clientWidth * 0.8;
    var step = page * (btn.classList.contains('yc-arrows__prev') ? -1 : 1);
    track.scrollBy({ left: step, behavior: 'smooth' });
  });

  // Scroll events don't bubble; listening in the capture phase catches every row.
  document.addEventListener('scroll', function (e) {
    var track = e.target && e.target.closest ? e.target.closest(ROWS) : null;
    if (!track) return;
    var arrows = track.closest('.rebuy-widget-content, .yc-gift-locked').querySelector(':scope > .yc-arrows');
    if (arrows) updateArrows(arrows, track);
  }, true);

  // ---------- Phones: "Add a little more" ----------
  // While the next free gift or free shipping is no more than phone.filler.within dollars away, a row at the top of
  // the cart offers best sellers that close the gap (templates/collection.cart-filler.liquid), those that close it
  // first. Gift tiers count the total after any multi-buy deal and free shipping the total before it, as the
  // progress bar does (applyDealBar); while USO is still answering for this cart, nothing is shown.

  var fillerCfg = (cfg.phone && cfg.phone.filler) || {};
  var fillerItems = null;   // the products, once loaded
  var fillerLoading = false;
  var fillerTried = 0;
  var FILLER_RETRY = 60000;

  function loadFiller() {
    if (fillerItems || fillerLoading || !fillerCfg.collection || Date.now() - fillerTried < FILLER_RETRY) return;
    fillerLoading = true;
    fillerTried = Date.now();
    fetch('/collections/' + encodeURIComponent(fillerCfg.collection) + '?view=cart-filler&sort_by=best-selling', { credentials: 'same-origin' })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.json(); })
      .then(function (list) {
        fillerItems = Array.isArray(list) ? list.filter(function (p) { return p && p.h && typeof p.p === 'number' && p.p > 0; }) : [];
        schedule();
      })
      .catch(function () {})
      .then(function () { fillerLoading = false; });
  }

  // The first tier not reached yet: { gap: cents to go, as the cart shows it; need: what a product must cost to reach
  // it on its own; reward: 'gift' | 'gift2' | 'shipping' }, null when every tier is reached, or WAIT while USO is still
  // answering for this cart. A gift tier is judged like its gift box (applyGifts): Rebuy's cart total less any deal,
  // against the box's own rule ("greater than $100" needs a cent over), so the strip, the box's "Spend $X more" and the
  // box opening agree. Free shipping goes by the bar (the total before any deal).
  var WAIT = {};
  function nextTier() {
    var sc = smartCart();
    var list = tiers();
    if (!list.length || !list.every(function (t) { return t.unlock_condition_type === 'price' && t.minimum != null; })) return null;
    if (usoOn() && !dealReady()) return WAIT;
    var base = null;
    try { base = sc.progressBarSubtotal(); } catch (e) {}
    if (typeof base !== 'number') return null;
    var off = dealCents();
    var after = Math.max(0, base - off);
    var spend = rebuySubtotal();
    if (spend !== null) spend = Math.max(0, spend - off);
    var gifts = 0;
    for (var i = 0; i < list.length; i++) {
      var min = null;
      try { min = sc.getTierMinimum(list[i]); } catch (e) {}
      if (typeof min !== 'number') min = Math.round(list[i].minimum * 100);
      var shipping = list[i].type === 'shipping';
      var reward = shipping ? 'shipping' : (++gifts > 1 ? 'gift2' : 'gift');
      if (shipping) {
        if (base < min) return { gap: min - base, need: min - base, reward: reward };
        continue;
      }
      var gift = (cfg.gifts || [])[gifts - 1];
      var pool = gift ? knownPool(gift.widget) : null;
      if (pool && spend !== null) {
        if (!earned(pool, spend)) {
          return {
            gap: Math.max(pool.threshold - spend, 1),
            need: pool.threshold - spend + (pool.operator === 'greater_than' ? 1 : 0),
            reward: reward
          };
        }
      } else if (after < min) {
        return { gap: min - after, need: min - after, reward: reward };
      }
    }
    return null;
  }

  // Products added from the strip, until they show in the cart (a second tap meanwhile would add another).
  var fillerAdding = {};
  var FILLER_ADDING_MS = 10000;
  function fillerBusy(handle) {
    return !!fillerAdding[handle] && Date.now() - fillerAdding[handle] < FILLER_ADDING_MS;
  }

  // Up to four products not in the cart yet: the best sellers that close the gap on their own, then the dearest of
  // the rest. One of each product type first (the best sellers are mostly lanyards), then repeats if short.
  var FILLER_SHOW = 4;
  function fillerPicks(need) {
    var inCart = {};
    lines().forEach(function (l) { inCart[l.handle] = true; });
    var list = fillerItems.filter(function (p) { return !inCart[p.h] && !fillerBusy(p.h); });
    var closes = list.filter(function (p) { return p.p >= need; });
    var rest = list.filter(function (p) { return p.p < need; }).sort(function (a, b) { return b.p - a.p; });
    var picks = [];
    var types = {};
    function take(source, distinct) {
      source.forEach(function (p) {
        var type = p.y || p.h;
        if (picks.length >= FILLER_SHOW || picks.indexOf(p) > -1 || (distinct && types[type])) return;
        picks.push(p);
        types[type] = true;
      });
    }
    take(closes, true);
    take(closes, false);
    take(rest, true);
    take(rest, false);
    return picks;
  }

  function fillerCard(p) {
    var card = el('div', 'yc-fill__card');
    var media = el('a', 'yc-fill__media');
    media.href = p.u;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (p.i) {
      var img = el('img');
      img.src = p.i;
      img.alt = '';
      img.width = 52;
      img.height = 52;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);
    var text = el('div', 'yc-fill__text');
    var title = el('a', 'yc-fill__title', p.t);
    title.href = p.u;
    text.appendChild(title);
    var price = el('p', 'yc-fill__price');
    if (p.c > p.p) {
      price.appendChild(el('span', 'yc-fill__sale', cents(p.p)));
      price.appendChild(el('s', 'yc-fill__was', cents(p.c)));
    } else {
      price.appendChild(el('span', null, cents(p.p)));
    }
    text.appendChild(price);
    card.appendChild(text);
    var add = el('button', 'yc-fill__add');
    add.type = 'button';
    add.setAttribute('data-yc-fill', p.h);
    var label = p.v ? (fillerCfg.add || 'Add {title} to cart')
      : p.z ? (fillerCfg.chooseSize || 'Choose a size for {title}') : (fillerCfg.choose || 'Choose options for {title}');
    add.setAttribute('aria-label', fill(label, { title: p.t }));
    card.appendChild(add);
    return card;
  }

  function applyFiller() {
    var body = root.querySelector('[data-rebuy-cart-anchor="body"]');
    var strip = root.querySelector('.yc-fill');
    var next = body && isPhone() && isAud() && fillerCfg.collection && lines().length ? nextTier() : null;
    // USO is checking the cart again (a quantity changed, something was added): the strip stays as it is meanwhile,
    // rather than going and coming back under the customer's finger.
    if (next === WAIT) return;
    var within = Math.round((Number(fillerCfg.within) || 0) * 100);
    if (next && next.gap <= within) loadFiller();
    var picks = next && next.gap <= within && fillerItems ? fillerPicks(next.need) : [];
    if (!picks.length) {
      if (strip) strip.remove();
      return;
    }
    var heading = fill(fillerCfg.heading || 'Add {amount} to unlock {reward}', {
      amount: cents(next.gap),
      reward: (fillerCfg.rewards || {})[next.reward] || ''
    });
    var key = heading + '|' + picks.map(function (p) { return p.h; }).join(',');
    if (strip && strip.getAttribute('data-yc-key') === key && body.firstElementChild === strip) return;
    if (!strip) {
      strip = el('section', 'yc-fill');
      strip.setAttribute('aria-labelledby', 'yc-fill-heading');
    }
    // Kept when only the gap changes, so the row isn't scrolled back to its start under the customer's finger.
    var sameItems = strip.getAttribute('data-yc-items') === picks.map(function (p) { return p.h; }).join(',');
    strip.setAttribute('data-yc-key', key);
    var head = strip.querySelector('.yc-fill__heading');
    if (!head) {
      head = el('p', 'yc-fill__heading');
      head.id = 'yc-fill-heading';
      strip.insertBefore(head, strip.firstChild);
    }
    setText(head, heading);
    if (!sameItems) {
      var row = strip.querySelector('.yc-fill__row');
      if (row) row.remove();
      row = el('div', 'yc-fill__row');
      picks.forEach(function (p) { row.appendChild(fillerCard(p)); });
      strip.appendChild(row);
      strip.setAttribute('data-yc-items', picks.map(function (p) { return p.h; }).join(','));
    }
    if (body.firstElementChild !== strip) body.insertBefore(strip, body.firstChild);
  }

  // Add: a single-variant product goes straight in; anything with choices opens the size panel (add mode). The line
  // carries _attribution so orders show what the strip sold. A refused add (sold out since loading) opens the page.
  // The product leaves the strip as soon as it is tapped (fillerBusy) and stays out until it is in the cart, so a
  // second tap can't add another; requests give up after 15 s (timed).
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart .yc-fill__add[data-yc-fill]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var handle = btn.getAttribute('data-yc-fill');
    var p = (fillerItems || []).filter(function (x) { return x.h === handle; })[0];
    if (!p || fillerBusy(handle)) return;
    var properties = {};
    if (fillerCfg.attribution) properties._attribution = fillerCfg.attribution;
    if (!p.v) {
      var card = btn.closest('.yc-fill__card');
      var img = card && card.querySelector('img');
      openPanel({ handle: p.h, product_title: p.t, url: p.u, image: img ? img.src : null, quantity: 1, properties: properties }, btn, 'add');
      return;
    }
    btn.disabled = true;
    fillerAdding[handle] = Date.now();
    timed('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(p.v), quantity: 1, properties: properties }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        try { window.Rebuy.Cart.fetchCart(); } catch (err) {}
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function () {
        delete fillerAdding[handle];
        window.location.href = p.u;
      })
      .then(function () {
        btn.disabled = false;
        schedule();
      });
  });

  // ---------- Empty cart ----------

  // "Popular right now" cards, rendered by templates/collection.cart-popular.liquid. Fetched once per page.
  var popular = null;
  function popularHtml() {
    if (!popular && cfg.popularCollection) {
      popular = fetch('/collections/' + encodeURIComponent(cfg.popularCollection) + '?view=cart-popular', { credentials: 'same-origin' })
        .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
        .then(function (html) { return html.indexOf('yc-pop__card') > -1 ? html : ''; })
        // A failed load is not cached: the next time Rebuy redraws the empty cart it tries again.
        .catch(function () { popular = null; return ''; });
    }
    return popular || Promise.resolve('');
  }

  function applyEmpty() {
    var box = root.querySelector('.rebuy-cart__flyout-empty-cart');
    if (!box || box.querySelector('.yc-empty')) return;
    var wrap = el('div', 'yc-empty');

    var intro = el('div', 'yc-empty__intro');
    if (assets.emptyIcon) {
      var icon = el('img', 'yc-empty__icon');
      icon.src = assets.emptyIcon;
      icon.alt = '';
      icon.width = 24;
      icon.height = 24;
      intro.appendChild(icon);
    }
    intro.appendChild(el('p', 'yc-empty__title', copy.emptyTitle || 'Your cart is empty'));
    wrap.appendChild(intro);

    var pop = el('div', 'yc-pop');
    pop.hidden = true;
    pop.appendChild(el('p', 'yc-pop__heading', copy.popularHeading || 'Popular right now'));
    var popBody = el('div');
    pop.appendChild(popBody);
    wrap.appendChild(pop);
    popularHtml().then(function (html) {
      if (!html) return;
      popBody.innerHTML = html;
      pop.hidden = false;
    });

    // Fixed to the bottom of the cart, like the Checkout button.
    var foot = el('div', 'yc-empty__footer');
    var button = el('button', 'yc-empty__continue', copy.continueShopping || 'Continue Shopping');
    button.type = 'button';
    button.addEventListener('click', function () {
      try { smartCart().hide(); } catch (e) {}
    });
    foot.appendChild(button);
    wrap.appendChild(foot);

    box.innerHTML = '';
    box.appendChild(wrap);
  }

  // + on a single-variant popular product adds it straight to the cart (Rebuy picks up the add and refreshes).
  // Products with sizes link to their product page instead, until the size panel exists (Phase 3).
  // If the add is refused (sold out since the page loaded), the customer goes to the product page.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('#rebuy-cart button.yc-pop__add[data-variant-id]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-variant-id')), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        try { window.Rebuy.Cart.fetchCart(); } catch (err) {}
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(function () {
        var url = btn.getAttribute('data-product-url');
        if (url) window.location.href = url;
      })
      .then(function () { btn.disabled = false; });
  });

  // ---------- Pre-order / custom item note ----------
  // The note the old Rebuy cart script used to show (it looked for a box that only the old cart template had, so it
  // stopped working with this cart). Wording: Theme settings > Preorder / Custom Cart Message.

  function needsNote(item) {
    if (item.properties && item.properties.preorder) return true;
    var tags = item.product && item.product.tags;
    if (typeof tags === 'string') tags = tags.split(/,\s*/);
    return Array.isArray(tags) && tags.some(function (t) { return /^(preorder|custom-item)$/i.test(t); });
  }

  function applyNote() {
    var items = root.querySelector('[data-rebuy-component="cart-items"]');
    var note = root.querySelector('.yc-note');
    if (!cfg.preorderNote || !items || !items.parentNode || !lines().some(needsNote)) {
      if (note) note.remove();
      return;
    }
    if (!note) {
      note = el('div', 'yc-note');
      note.setAttribute('role', 'note');
      // Shop content from the theme settings (rich text).
      note.innerHTML = cfg.preorderNote;
      items.parentNode.insertBefore(note, items.nextSibling);
    }
  }

  // ---------- Login and Yarn Rewards points ----------

  // The logged-in customer's balance. First from Yotpo Loyalty's own API (window.swellAPI), trusted only once it has
  // loaded the customer (their email is set: a guest gets a blank record with 0 points). Otherwise from the hidden
  // Yotpo widget the header's rewards card reads. null = not known (yet).
  function pointsBalance() {
    try {
      var details = window.swellAPI && typeof window.swellAPI.getCustomerDetails === 'function' ? window.swellAPI.getCustomerDetails() : null;
      if (details && details.email && typeof details.pointsBalance === 'number') return details.pointsBalance;
    } catch (e) {}
    var source = document.querySelector('[data-loyalty-source] .yotpo-description-points-balance');
    var match = source && (source.textContent || '').match(/\d[\d,]*/);
    return match ? parseInt(match[0].replace(/,/g, ''), 10) : null;
  }

  // How long to wait for the balance before showing the plain "View your Yarn Rewards points" line instead.
  var POINTS_WAIT = 8000;
  var pageStarted = Date.now();

  // Copy line -> nodes: [text](login|rewards) becomes a link, {points} the balance in bold.
  function renderLine(text, points) {
    var p = el('p');
    var pattern = /\[([^\]]+)\]\((\w+)\)|\{points\}/g;
    var last = 0;
    var m;
    while ((m = pattern.exec(text))) {
      if (m.index > last) p.appendChild(document.createTextNode(text.slice(last, m.index)));
      if (m[1]) {
        var a = el('a', null, m[1]);
        a.href = (cfg.points && cfg.points[m[2]]) || '#';
        p.appendChild(a);
      } else {
        p.appendChild(el('strong', null, String(points)));
      }
      last = pattern.lastIndex;
    }
    if (last < text.length) p.appendChild(document.createTextNode(text.slice(last)));
    return p;
  }

  function pointsCopy(empty) {
    var set = cfg.points || {};
    var prefix = empty ? 'empty' : 'items';
    if (!cfg.customer) return { lines: set[prefix + 'Guest'] };
    var points = pointsBalance();
    // Yotpo not loaded (yet): nothing rather than a wrong number; after a while, a plain link to the rewards page.
    if (points === null) return Date.now() - pageStarted >= POINTS_WAIT ? { lines: set.memberUnknown } : null;
    return { lines: points > 0 ? set[prefix + 'Member'] : set[prefix + 'New'], points: points };
  }

  function placePoints(parent, before, empty) {
    var line = parent.querySelector(':scope > .yc-points');
    var wanted = pointsCopy(empty);
    var signature = wanted && wanted.lines ? JSON.stringify(wanted) : '';
    if (!signature) { if (line) line.remove(); return; }
    if (line && line.getAttribute('data-yc-sig') === signature) return;
    var fresh = el('div', 'yc-points' + (empty ? ' yc-points--empty' : ''));
    fresh.setAttribute('data-yc-sig', signature);
    wanted.lines.forEach(function (text) { fresh.appendChild(renderLine(text, wanted.points)); });
    if (line) line.replaceWith(fresh);
    else parent.insertBefore(fresh, before || null);
  }

  function applyPoints() {
    var intro = root.querySelector('.yc-empty__intro');
    if (intro) placePoints(intro, null, true);
    var items = root.querySelector('[data-rebuy-component="cart-items"]');
    // Under the items, after the pre-order note when there is one.
    var after = items && items.nextSibling;
    if (after && after.classList && after.classList.contains('yc-note')) after = after.nextSibling;
    if (items && items.parentNode && lines().length) placePoints(items.parentNode, after, false);
    else {
      var stray = root.querySelector('.yc-points:not(.yc-points--empty)');
      if (stray) stray.remove();
    }
  }

  // ---------- Footer ----------

  // The line's compare-at price in cents, read from Rebuy's product data (dollars there). Rebuy's own compareAtPrice()
  // mixes cents and dollars depending on the line, so it is not used. 0 until Rebuy has loaded the product.
  function compareAtCents(item) {
    try {
      if (!window.Rebuy.Cart.isEnriched(item)) return 0;
      var variant = item.product && item.product.selected_variant;
      var value = variant ? Math.round(parseFloat(variant.compare_at_price) * 100) : 0;
      return isFinite(value) ? value : 0;
    } catch (e) {
      return 0;
    }
  }

  // Sale savings on paid lines (compare-at over the price before discounts) plus every discount Shopify applied
  // (cart.total_discount covers line and order discounts). Free gifts are left out. AUD only.
  function savedCents() {
    var cart = shopifyCart();
    if (!cart || !isAud()) return 0;
    var saved = typeof cart.total_discount === 'number' ? cart.total_discount : 0;
    lines().forEach(function (item) {
      // A gift brought to $0 by a discount: that discount is in cart.total_discount, so it comes back out.
      if (isGift(item)) {
        if (item.original_line_price > item.final_line_price) saved -= item.original_line_price - item.final_line_price;
        return;
      }
      var compare = compareAtCents(item);
      if (compare > item.original_price) saved += (compare - item.original_price) * item.quantity;
    });
    return saved;
  }

  function applyFooter() {
    var area = root.querySelector('[data-rebuy-component="checkout-area"]');
    // Prices include GST, so only shipping is added at checkout, and not once free shipping is reached.
    // Other currencies: prices there may not include Australian GST, so the wording keeps taxes in.
    var note = !isAud() && copy.shippingNoteOther ? copy.shippingNoteOther
      : freeShipping() && copy.shippingFree ? copy.shippingFree : copy.shippingNote;
    if (area && note) setAttr(area, 'data-yc-note', note);
    else dropAttr(area, 'data-yc-note');

    var subtotal = root.querySelector('[data-rebuy-component="cart-subtotal"] .rebuy-cart__flyout-subtotal');
    var off = dealCents();
    var totalCents = null;
    if (subtotal) {
      // Multi-buy deals: a line each above Total ("2 for $80 deal −$22.51"), and Total after the deal.
      var amountEl = subtotal.querySelector('.rebuy-cart__flyout-subtotal-amount');
      // The figure Rebuy shows as Total (the cart's total after Shopify discounts).
      var cartNow = shopifyCart();
      totalCents = cartNow && typeof cartNow.total_price === 'number' ? cartNow.total_price : null;
      var rows = subtotal.parentNode.querySelector('.yc-deal-rows');
      var groups = [];
      if (off) {
        var byName = {};
        deal.bundles.forEach(function (b) {
          var named = dealName(b.names.length, b.orig - b.save);
          if (!byName[named]) { byName[named] = { deal: named, save: 0, count: 0 }; groups.push(byName[named]); }
          byName[named].save += b.save;
          byName[named].count++;
        });
        // "2 for $80 deal", or "2 for $80 deal × 2" when the same deal is reached more than once.
        groups.forEach(function (g) {
          g.name = g.count > 1 ? fill(uso.rowMany || '{deal} deal × {count}', { deal: g.deal, count: g.count })
            : fill(uso.row || '{deal} deal', { deal: g.deal });
        });
        var listed = groups.reduce(function (n, g) { return n + g.save; }, 0);
        if (off - listed > 0) groups.push({ name: uso.otherOffer || 'Special offer', save: off - listed });
      }
      var rowsKey = JSON.stringify(groups);
      if (!groups.length) {
        if (rows) rows.remove();
      } else if (!rows || rows.getAttribute('data-yc-rows') !== rowsKey) {
        if (!rows) { rows = el('div', 'yc-deal-rows'); subtotal.parentNode.insertBefore(rows, subtotal); }
        rows.setAttribute('data-yc-rows', rowsKey);
        rows.textContent = '';
        groups.forEach(function (g) {
          var r = el('div', 'yc-deal-row');
          r.appendChild(el('span', 'yc-deal-row__label', g.name));
          r.appendChild(el('span', 'yc-deal-row__amount', '−' + cents(g.save)));
          rows.appendChild(r);
        });
      }
      if (amountEl && off && totalCents !== null) setAttr(amountEl, 'data-yc-deal-total', cents(Math.max(0, totalCents - off)));
      else dropAttr(amountEl, 'data-yc-deal-total');

      var row = subtotal.parentNode.querySelector('.yc-saved');
      var saved = savedCents() + off;
      if (saved > 0) {
        if (!row) {
          row = el('div', 'yc-saved');
          row.appendChild(el('span', 'yc-saved__label', copy.saved || 'You saved'));
          row.appendChild(el('span', 'yc-saved__amount'));
          subtotal.parentNode.insertBefore(row, subtotal.nextSibling);
        }
        setText(row.lastChild, cents(saved));
      } else if (row) {
        row.remove();
      }
    }

    var line = root.querySelector('.rebuy-cart__flyout-installments');
    if (!line || !copy.installments) return;
    var text = line.textContent || '';
    var amount = (text.match(/\$[\d,]+\.\d{2}/) || [])[0];
    var count = (text.match(/\b(\d+)\s+(?:interest-free\s+installments|payments)\b/) || [])[1];
    if (!amount || !count) return;
    // From the cart's total (less any deal: checkout's total), not read back from this line, which may hold an
    // earlier rewrite.
    if (totalCents) amount = cents(Math.round(Math.max(0, totalCents - off) / parseInt(count, 10)));
    var wanted = copy.installments.replace('{count}', count).replace('{amount}', amount);
    // Done already: our wording plus the badge (or no badge configured). Stops the rewrite repeating.
    if (text.trim() === wanted && (!assets.afterpay || line.querySelector('.yc-afterpay-badge'))) return;
    var terms = line.querySelector('a[href]');
    var termsUrl = terms ? terms.getAttribute('href') : '';
    line.textContent = wanted;
    if (assets.afterpay) {
      var badge = el('img', 'yc-afterpay-badge');
      badge.src = assets.afterpay;
      badge.alt = 'Afterpay';
      badge.width = 34;
      badge.height = 23;
      // Keep Rebuy's link to Afterpay's instalment terms on the badge.
      if (termsUrl) {
        var link = el('a');
        link.href = termsUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.appendChild(badge);
        line.appendChild(link);
      } else {
        line.appendChild(badge);
      }
    }
  }

  function apply() {
    queued = false;
    attach();
    if (!root) return;
    var steps = [applyTitle, applyProgress, applyLines, applyNewLines, applyDeals, applyDealBar, applyGifts, applyFiller, applyRecs, applyArrows, applyEmpty, applyNote, applyPoints, applyFooter, applyBand];
    for (var i = 0; i < steps.length; i++) {
      try { steps[i](); } catch (e) { if (window.console) console.info('[cart-redesign]', e); }
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    // A timer, not requestAnimationFrame: animation frames pause in background tabs.
    setTimeout(apply, 30);
  }

  // Rebuy mounts its cart by replacing the #rebuy-cart placeholder, so the watcher follows whichever element is current.
  var cartObserver = new MutationObserver(schedule);
  function attach() {
    var current = document.getElementById('rebuy-cart');
    if (current === root) return;
    cartObserver.disconnect();
    root = current;
    if (root) cartObserver.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('rebuy:cart.change', schedule);
  document.addEventListener('rebuy:smartcart.show', schedule);
  // Rebuy's gift boxes show or hide themselves about half a second after a cart change by changing their inline
  // style, which the watcher above does not follow (it would see our own style writes too). Look again then.
  ['rebuy:cart.change', 'rebuy:smartcart.show'].forEach(function (name) {
    document.addEventListener(name, function () { setTimeout(schedule, 700); setTimeout(schedule, 1500); });
  });
  // The swap wording (after Change) lasts for this visit to the cart.
  document.addEventListener('rebuy:smartcart.hide', function () {
    Object.keys(swap).forEach(function (k) { delete swap[k]; });
  });
  // Turning a phone or resizing across 768px switches the round 2 behaviours on or off.
  if (PHONE) {
    if (PHONE.addEventListener) PHONE.addEventListener('change', schedule);
    else if (PHONE.addListener) PHONE.addListener(schedule);
  }

  // The points balance arrives whenever Yotpo finishes loading.
  function watchPoints() {
    var source = document.querySelector('[data-loyalty-source]');
    if (!source) return;
    var watcher = new MutationObserver(function () {
      schedule();
      if (pointsBalance() !== null) watcher.disconnect();
    });
    watcher.observe(source, { childList: true, subtree: true, characterData: true });
    setTimeout(function () { watcher.disconnect(); }, 20000);
  }

  function start() {
    // Watching body's direct children is cheap and catches the cart being added or replaced.
    new MutationObserver(function () {
      if (document.getElementById('rebuy-cart') !== root) schedule();
    }).observe(document.body, { childList: true });
    if (cfg.customer) {
      watchPoints();
      // Yotpo Loyalty announces when it has loaded the customer; and the fallback line needs one more look later.
      ['swell:setup', 'swell:initialized'].forEach(function (name) { document.addEventListener(name, schedule); });
      setTimeout(schedule, POINTS_WAIT + 500);
    }
    schedule();
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
